import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { productUrl } from '../utils/urls';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs'; // Keep for OrdersSectionTabs child component
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { motion } from 'framer-motion';
import { 
  Package, 
  Image as ImageIcon, 
  Store,
  Sparkles,
  FileText,
  Crown,
  Check,
  X,
  MessageSquare,
  Trash2,
  Eye,
  Truck,
  PlayCircle,
  CheckCircle2,
  Euro,
  Clock,
  Edit,
  Plus,
  History,
  Calendar,
  Play,
  Award,
  Lock,
  Warehouse,
  Save,
  ShoppingBag,
  MapPin,
  User,
  Star,
  Send,
  Reply,
  AlertTriangle,
  ExternalLink,
  ShoppingCart,
  Palette,
  Hammer,
  Info,
  Wallet,
  Banknote,
  Shield,
  Building,
  RefreshCw,
  ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';

import { supabaseUpload } from '../lib/api';
import { ImageUploader } from '../components/ImageUploader';
import { MultiImageUploader } from '../components/MultiImageUploader';
import ShopCustomizer, { AdvancedStorefrontTeaser } from '../components/dashboard/ShopCustomizer';
import { parseVideo } from '../utils/video';

// Suggerimenti rapidi per "Materiali e tecniche"
const MATERIAL_SUGGESTIONS = [
  'Legno', 'Ceramica', 'Gres', 'Porcellana', 'Vetro', 'Pelle', 'Cuoio', 'Lana', 'Lino', 'Cotone', 'Seta',
  'Argento', 'Ottone', 'Ferro battuto', 'Carta', 'Resina', 'Pietra', 'Marmo', 'Tornio', 'Intaglio', 'Ricamo',
  'Tessitura a mano', 'Incisione', 'Pittura a mano', 'Stampa 3D', 'Tintura naturale', 'Materiali di recupero',
];
import { clearShopCache } from '../lib/shopCache';
import StorefrontModeSwitch from '../components/dashboard/StorefrontModeSwitch';
import ShopStats from '../components/dashboard/ShopStats';
import { useMyShop } from '../hooks/useMyShop';
import { saveMyShop } from '../lib/shopsApi';
import { ProductForm } from '../components/ProductForm';
import { validatePIVA, formatPIVA } from '../components/CrafterBadge';
import { AlertCircle, Briefcase, Heart, CreditCard, Users, Percent, Gift, Inbox, Upload, Loader2, ChevronDown, ChevronUp, Images, Globe, XCircle } from 'lucide-react';
import { getIconComponent } from '../lib/categoryIcons';
import StripeConnectCard from '../components/StripeConnectCard';
import ShipmentConfirmDialog from '../components/ShipmentConfirmDialog';
import ShippingRulesManager from '../components/ShippingRulesManager';
import OrderItemCustomizationBlock from '../components/OrderItemCustomizationBlock';

const TIMELINE_OPTIONS = [
  { value: 'immediate', label: 'Disponibilità immediata' },
  { value: '1-2days', label: '1-2 giorni' },
  { value: '3-5days', label: '3-5 giorni' },
  { value: '1week', label: '1 settimana' },
  { value: '1-2weeks', label: '1-2 settimane' },
  { value: '3-4weeks', label: '3-4 settimane' },
  { value: 'over-month', label: 'Oltre un mese' },
];

// Upgrade to Artigiano Dialog Component
function UpgradeToArtigianoDialog({ profile, onSuccess }) {
  const [open, setOpen] = React.useState(false);
  const [piva, setPiva] = React.useState('');
  const [pivaError, setPivaError] = React.useState('');
  const [loading, setLoading] = React.useState(false);

  const handlePivaChange = (value) => {
    setPiva(value);
    if (value) {
      const validation = validatePIVA(value);
      setPivaError(validation.valid ? '' : validation.error);
    } else {
      setPivaError('');
    }
  };

  const handleUpgrade = async () => {
    const validation = validatePIVA(piva);
    if (!validation.valid) {
      setPivaError(validation.error);
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ 
          crafter_type: 'professionista',
          partita_iva: validation.cleaned
        })
        .eq('id', profile.id);

      if (error) throw error;
      
      toast.success('Congratulazioni! Ora sei un Artigiano Professionista 🎉');
      setOpen(false);
      onSuccess();
    } catch (error) {
      toast.error('Errore durante l\'upgrade: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="w-full border-red-300 text-red-600 hover:bg-red-50" data-testid="open-upgrade-professional-btn">
          <Briefcase className="w-4 h-4 mr-2" />
          Diventa Artigiano
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="text-2xl">🔴</span>
            Diventa Artigiano Professionista
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-800">
              <strong>Attenzione:</strong> Per diventare Artigiano Professionista devi avere una Partita IVA italiana valida.
              Questa operazione è irreversibile.
            </p>
          </div>

          <div>
            <Label htmlFor="upgrade-piva" className="mb-2 block font-medium">
              Partita IVA <span className="text-red-500">*</span>
            </Label>
            <Input
              id="upgrade-piva"
              type="text"
              value={piva}
              onChange={(e) => handlePivaChange(e.target.value)}
              placeholder="12345678901"
              className={pivaError ? 'border-red-500' : ''}
              maxLength={14}
            />
            {pivaError && (
              <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {pivaError}
              </p>
            )}
            {piva && !pivaError && (
              <p className="text-sm text-emerald-600 mt-1 flex items-center gap-1">
                <Check className="w-4 h-4" />
                Formato P.IVA valido: {formatPIVA(piva)}
              </p>
            )}
          </div>

          <div className="bg-slate-50 rounded-lg p-4">
            <p className="font-medium mb-2">Cosa otterrai:</p>
            <ul className="space-y-2 text-sm">
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 text-red-500" />
                Badge "Artigiano P.IVA Verificato"
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 text-red-500" />
                Maggiore credibilità e fiducia
              </li>
            </ul>
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setOpen(false)}
            >
              Annulla
            </Button>
            <Button
              className="flex-1 bg-red-500 hover:bg-red-600 text-white"
              onClick={handleUpgrade}
              disabled={loading || !piva || !!pivaError}
              data-testid="upgrade-professional-submit-btn"
            >
              {loading ? 'Upgrade in corso...' : 'Conferma Upgrade'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function CrafterDashboardContent({ 
  viewMode = null, // 'orders' | 'shipping' | 'products' | 'inventory' | 'requests' | 'proposals' | 'fans' | 'storefront' | 'feedback' | null (show all with tabs)
  initialOrdersView = 'active' // 'active' | 'completed' | 'in_progress' - only used when viewMode='orders'
}) {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  
  // Map viewMode to tab values
  const getTabFromViewMode = (mode) => {
    const mapping = {
      'orders': 'orders',
      'shipping': 'shipping',
      'stripe-setup': 'stripe-setup',
      'payouts': 'payouts',
      'products': 'products',
      'inventory': 'inventory',
      'requests': 'direct-requests',
      'proposals': 'proposals',
      'fans': 'fans',
      'storefront': 'storefront',
      'feedback': 'feedback'
    };
    return mapping[mode] || 'storefront';
  };
  
  // Main tab state - persisted in sessionStorage to survive tab switching
  const [mainTab, setMainTab] = useState(() => {
    if (viewMode) return getTabFromViewMode(viewMode);
    return sessionStorage.getItem('crafterDashboardTab') || 'storefront';
  });
  
  // Sync mainTab with viewMode changes
  useEffect(() => {
    if (viewMode) {
      setMainTab(getTabFromViewMode(viewMode));
    }
  }, [viewMode]);

  // Vetrina base / vetrina avanzata (Pro, o tutti durante il lancio)
  const myShop = useMyShop(mainTab === 'storefront');
  const [advancedView, setAdvancedView] = useState(() => (new URLSearchParams(window.location.search).get('vista') === 'statistiche' ? 'stats' : 'editor'));
  const canCustomizeStorefront = !!myShop.shop?.canCustomize;
  // I social in vetrina sono un vantaggio Pro, inclusi durante il lancio
  const canEditSocial = profile?.tier === 'pro' || canCustomizeStorefront;
  const storefrontLiveMode = canCustomizeStorefront && myShop.shop?.is_published ? 'advanced' : 'base';
  const [storefrontModeChoice, setStorefrontModeChoice] = useState(null);
  const storefrontMode = storefrontModeChoice || storefrontLiveMode;
  const [switchingStorefront, setSwitchingStorefront] = useState(false);

  const handleSelectStorefrontMode = async (next) => {
    if (next === storefrontMode) return;
    // Tornare alla base con l'avanzata pubblicata: la si nasconde ai clienti (la configurazione resta)
    if (next === 'base' && storefrontLiveMode === 'advanced') {
      setSwitchingStorefront(true);
      try {
        const res = await saveMyShop({ is_published: false });
        myShop.applySaved(res);
        toast.success('Ora i clienti vedono la vetrina base. La vetrina avanzata resta salvata e puoi riattivarla quando vuoi.');
      } catch (e) {
        toast.error(e.message);
        return;
      } finally {
        setSwitchingStorefront(false);
      }
    }
    setStorefrontModeChoice(next);
  };
  
  // Handle main tab change with persistence
  const handleMainTabChange = (value) => {
    if (viewMode) return; // Don't allow tab change in single-view mode
    setMainTab(value);
    sessionStorage.setItem('crafterDashboardTab', value);
  };
  
  // State
  const [products, setProducts] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [matchingRequests, setMatchingRequests] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [orders, setOrders] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [productFans, setProductFans] = useState([]); // Users who saved crafter's products
  const [directRequests, setDirectRequests] = useState([]); // Direct requests to this crafter
  const [loading, setLoading] = useState(true);
  const [ordersView, setOrdersView] = useState(initialOrdersView === 'in_progress' ? 'active' : initialOrdersView); // 'active' or 'completed'
  const [ordersStatusFilter, setOrdersStatusFilter] = useState(initialOrdersView === 'in_progress' ? 'in_progress' : null); // specific status filter
  const [expandedOrderId, setExpandedOrderId] = useState(null); // Track expanded order in active view
  const [proposalsView, setProposalsView] = useState('pending'); // 'pending' or 'history'
  const [expandedProposalId, setExpandedProposalId] = useState(null); // Track expanded proposal
  const [directRequestsView, setDirectRequestsView] = useState('pending'); // 'pending' or 'history'
  const [expandedRequestId, setExpandedRequestId] = useState(null); // Track expanded direct request
  
  // Ref to track if initial data has been loaded (prevents reload on tab switch)
  const dataLoadedRef = useRef(false);
  const userIdRef = useRef(null);
  
  // Progress images state
  const [progressImages, setProgressImages] = useState([]);
  const [progressMessage, setProgressMessage] = useState('');
  const [uploadingProgress, setUploadingProgress] = useState(false);
  const [sendingProgress, setSendingProgress] = useState(false);
  const [showNoImagesWarning, setShowNoImagesWarning] = useState(false);
  const [pendingReadyOrderId, setPendingReadyOrderId] = useState(null);
  const [addToPortfolio, setAddToPortfolio] = useState(true);
  
  // Direct request response state
  const [respondingToRequest, setRespondingToRequest] = useState(null);
  const [requestResponseForm, setRequestResponseForm] = useState({
    price: '',
    timeline: '',
    message: '',
    shippingCost: '',
    freeShipping: false
  });
  const [responseAttachments, setResponseAttachments] = useState([]);
  const [uploadingResponseAttachment, setUploadingResponseAttachment] = useState(false);
  const [sendingRequestResponse, setSendingRequestResponse] = useState(false);

  // Handle file upload for response
  const handleResponseFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    if (responseAttachments.length + files.length > 5) {
      toast.error('Massimo 5 immagini consentite');
      return;
    }

    setUploadingResponseAttachment(true);
    const newAttachments = [];

    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} supera i 5MB`);
        continue;
      }

      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} non è un'immagine valida`);
        continue;
      }

      const fileExt = file.name.split('.').pop();
      const fileName = `proposal-images/${user.id}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;

      try {
        const { publicUrl } = await supabaseUpload('rarifex-uploads', fileName, file);
        newAttachments.push({
          name: file.name,
          url: publicUrl,
          type: file.type,
        });
      } catch (error) {
        console.error('Upload error:', error);
        toast.error(`Errore upload ${file.name}`);
      }
    }

    setResponseAttachments([...responseAttachments, ...newAttachments]);
    setUploadingResponseAttachment(false);
    
    if (newAttachments.length > 0) {
      toast.success(`${newAttachments.length} immagine/i caricata/e`);
    }
  };

  const removeResponseAttachment = (index) => {
    setResponseAttachments(responseAttachments.filter((_, i) => i !== index));
  };
  
  // Product fans / discount offer state
  const [offerDiscountDialog, setOfferDiscountDialog] = useState(null); // { userId, productId, productTitle }
  const [discountPercentage, setDiscountPercentage] = useState('10');
  const [discountMessage, setDiscountMessage] = useState('');
  const [sendingOffer, setSendingOffer] = useState(false);
  const [startingFanChat, setStartingFanChat] = useState(null);
  
  // Stripe Connect state
  const [stripeConnected, setStripeConnected] = useState(false);
  const [stripeLoading, setStripeLoading] = useState(true);
  const [stripeAccountDetails, setStripeAccountDetails] = useState(null);
  const [pendingBalance, setPendingBalance] = useState({ amount: 0, ordersCount: 0 });
  
  // Review response state
  const [respondingToReview, setRespondingToReview] = useState(null);
  const [responseText, setResponseText] = useState('');
  const [sendingResponse, setSendingResponse] = useState(false);
  
  // Vetrina state
  const [storefrontTitle, setStorefrontTitle] = useState('');
  const [storefrontMotto, setStorefrontMotto] = useState('');
  const [storefrontSectionName, setStorefrontSectionName] = useState('La Bottega');
  const [storefrontDescription, setStorefrontDescription] = useState('');
  const [storefrontImage, setStorefrontImage] = useState('');
  
  // Advanced Storefront state (Pro Artigiano only)
  const [workshopStory, setWorkshopStory] = useState('');
  const [foundedYear, setFoundedYear] = useState('');
  const [presentationVideo, setPresentationVideo] = useState('');
  const [materials, setMaterials] = useState([]);
  const [newMaterial, setNewMaterial] = useState('');
  const [certifications, setCertifications] = useState([]);
  const [newCertification, setNewCertification] = useState('');
  const [coverImages, setCoverImages] = useState([]);
  
  // Social Media Links state
  const [socialLinks, setSocialLinks] = useState({
    website: '',
    instagram: '',
    facebook: '',
    tiktok: '',
    youtube: '',
    pinterest: '',
    linkedin: ''
  });
  
  // Add Product state
  const [isProductDialogOpen, setIsProductDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null); // null = create, object = edit
  const [productTitle, setProductTitle] = useState('');
  const [productDescription, setProductDescription] = useState('');
  const [baseImages, setBaseImages] = useState([]);
  const [mainImageIndex, setMainImageIndex] = useState(0);
  const [basePrice, setBasePrice] = useState('');
  const [crafterDiscount, setCrafterDiscount] = useState(''); // Sconto artigiano in percentuale
  const [baseTimeline, setBaseTimeline] = useState('immediate');
  const [commissionTermsAccepted, setCommissionTermsAccepted] = useState(false); // Accettazione termini commissione
  const [productCategory, setProductCategory] = useState('');
  
  // Variants
  const [hasVariants, setHasVariants] = useState(false);
  const [variants, setVariants] = useState([]);
  
  // Customizations
  const [hasCustomizations, setHasCustomizations] = useState(false);
  const [customizations, setCustomizations] = useState([]);
  
  // Inventory (for immediate availability)
  const [inventory, setInventory] = useState({});
  
  // Category
  const [selectedCategory, setSelectedCategory] = useState(null);
  
  // Shipping Rules
  const [shippingRules, setShippingRules] = useState([]);
  const [selectedShippingRuleId, setSelectedShippingRuleId] = useState(null);
  
  // Highlights (Punti di Forza)
  const [productHighlights, setProductHighlights] = useState([]);
  
  // Categories from database for ProductForm
  const [formCategories, setFormCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  
  // Form step (1 = details, 2 = inventory)
  const [productFormStep, setProductFormStep] = useState(1);
  
  // Portfolio state
  const [isPortfolioDialogOpen, setIsPortfolioDialogOpen] = useState(false);
  const [portfolioTitle, setPortfolioTitle] = useState('');
  const [portfolioDescription, setPortfolioDescription] = useState('');
  const [portfolioImage, setPortfolioImage] = useState('');
  const [portfolioCategory, setPortfolioCategory] = useState('');
  
  // Request response state
  const [responseMessage, setResponseMessage] = useState('');
  const [rejectReason, setRejectReason] = useState('');

  // Plan limits - Now weekly for proposals, no limit on products
  const MAX_PROPOSALS_FREE_WEEK = 5;
  
  // Check premium features status
  const [premiumFeaturesEnabled, setPremiumFeaturesEnabled] = useState(true);
  
  useEffect(() => {
    const saved = localStorage.getItem('rarifex_premium_features_enabled');
    setPremiumFeaturesEnabled(saved !== 'false');
    
    const handleChange = (event) => {
      setPremiumFeaturesEnabled(event.detail.enabled);
    };
    window.addEventListener('premiumFeaturesChanged', handleChange);
    return () => window.removeEventListener('premiumFeaturesChanged', handleChange);
  }, []);
  
  // Everyone gets premium benefits if premium features are disabled globally
  const isPremiumUser = !premiumFeaturesEnabled || profile?.tier === 'pro';

  // Check if user can create more products - NOW UNLIMITED FOR ALL
  const canCreateProduct = () => {
    return { allowed: true };
  };

  // Check if user can send more proposals this week
  const canSendProposal = () => {
    if (isPremiumUser) return { allowed: true };
    
    const proposalsThisWeek = profile?.proposals_this_week || 0;
    const resetDate = profile?.proposals_reset_date ? new Date(profile.proposals_reset_date) : null;
    const now = new Date();
    
    // Check if we need to reset the counter (new week - Monday)
    if (resetDate) {
      const daysSinceReset = Math.floor((now - resetDate) / (1000 * 60 * 60 * 24));
      if (daysSinceReset >= 7) {
        // Counter should be reset - allow the proposal
        return { allowed: true, needsReset: true };
      }
    }
    
    if (proposalsThisWeek >= MAX_PROPOSALS_FREE_WEEK) {
      return { 
        allowed: false, 
        message: `Hai raggiunto il limite di ${MAX_PROPOSALS_FREE_WEEK} risposte a richieste questa settimana. Passa a Premium per risposte illimitate!`
      };
    }
    return { allowed: true, remaining: MAX_PROPOSALS_FREE_WEEK - proposalsThisWeek };
  };

  // Increment proposal counter
  const incrementProposalCounter = async () => {
    try {
      if (isPremiumUser) return; // Premium users don't need tracking
      
      const proposalCheck = canSendProposal();
      const currentCount = proposalCheck.needsReset ? 0 : (profile?.proposals_this_week || 0);
      
      await supabase
        .from('profiles')
        .update({ 
          proposals_this_week: currentCount + 1,
          proposals_reset_date: new Date().toISOString()
        })
        .eq('id', user.id);
    } catch (error) {
      console.error('Error updating proposal counter:', error);
    }
  };
  
  // Filter matching requests for free users (24h delay)
  const getVisibleRequests = (requests) => {
    if (isPremiumUser) return requests;
    
    const now = new Date();
    return requests.filter(request => {
      const createdAt = new Date(request.created_at);
      const hoursSinceCreation = (now - createdAt) / (1000 * 60 * 60);
      return hoursSinceCreation >= 24;
    });
  };

  useEffect(() => {
    // Only load data if:
    // 1. User exists AND
    // 2. Either data hasn't been loaded yet OR user ID has changed (different user logged in)
    if (user && (!dataLoadedRef.current || userIdRef.current !== user.id)) {
      userIdRef.current = user.id;
      dataLoadedRef.current = true;
      
      loadDashboardData();
      loadStorefront();
      loadOrders();
      loadReviews();
      loadProductFans();
      loadDirectRequests();
      loadShippingRules();
      loadFormCategories();
      checkStripeStatus();
    }
  }, [user]);

  // Send Stripe reminder when data is loaded and conditions are met
  useEffect(() => {
    if (!stripeLoading && pendingBalance.ordersCount > 0 && !stripeConnected && profile?.email) {
      maybeSendStripeReminder(stripeConnected, true, pendingBalance.amount, pendingBalance.ordersCount);
    }
  }, [stripeLoading, stripeConnected, pendingBalance, profile]);

  // Refetch shipping rules ogni volta che il dialog di aggiunta prodotto si apre
  // Garantisce che il banner "nessuna regola" scompaia subito dopo che l'utente
  // ne crea una tramite la CTA.
  useEffect(() => {
    if (isProductDialogOpen && user) {
      loadShippingRules();
    }
  }, [isProductDialogOpen]);

  // Auto-apri dialog "Aggiungi creazione" se veniamo dal flow onboarding regola
  // di spedizione (sessionStorage flag settato dalla CTA del banner).
  useEffect(() => {
    if (!user) return;
    const pending = sessionStorage.getItem('pending_product_creation');
    if (pending === '1') {
      sessionStorage.removeItem('pending_product_creation');
      // Piccolo delay per assicurare che user/data siano caricati
      setTimeout(() => setIsProductDialogOpen(true), 200);
    }
  }, [user]);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const loadShippingRules = async () => {
    try {
      const response = await fetch(`${API_URL}/api/shipping-rules/${user.id}`);
      if (response.ok) {
        const data = await response.json();
        setShippingRules(data.rules || []);
        // Set default shipping rule if available
        const defaultRule = data.rules?.find(r => r.is_default && r.is_active);
        if (defaultRule) {
          setSelectedShippingRuleId(defaultRule.id);
        }
      }
    } catch (error) {
      console.error('Error loading shipping rules:', error);
    }
  };

  // Load categories from database for ProductForm
  // Uses /categories/public endpoint to only show published categories (is_active=true)
  const loadFormCategories = async () => {
    setCategoriesLoading(true);
    try {
      // Use /categories/public endpoint to get only published categories
      const response = await fetch(`${API_URL}/api/categories/public`);
      if (response.ok) {
        const data = await response.json();
        const rawCategories = data.categories || [];
        
        // Build tree structure for ProductForm
        const mainCategories = rawCategories
          .filter(c => !c.parent_id)
          .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
        
        // Map icon names to components (dynamic mapping via lib/categoryIcons)
        // Copre tutte le icone Lucide + alias noti (Home->House, GiftIcon->Gift, ecc)
        
        const treeCategories = mainCategories.map(main => {
          const subcategories = rawCategories
            .filter(c => c.parent_id === main.id)
            .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
            .map(sub => {
              const children = rawCategories
                .filter(c => c.parent_id === sub.id)
                .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
                .map(child => ({
                  name: child.name,
                  slug: child.slug
                }));

              return {
                id: sub.id,
                name: sub.name,
                slug: sub.slug,
                icon: getIconComponent(sub.icon),
                children
              };
            });

          return {
            id: main.slug,
            name: main.name,
            icon: getIconComponent(main.icon),
            subcategories
          };
        });
        
        setFormCategories(treeCategories);
      }
    } catch (error) {
      console.error('Error loading categories:', error);
    } finally {
      setCategoriesLoading(false);
    }
  };

  const checkStripeStatus = async () => {
    setStripeLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/connect/account-status/${user.id}`);
      if (response.ok) {
        const data = await response.json();
        setStripeConnected(data.has_account && data.charges_enabled);
        setStripeAccountDetails(data);
      }
      
      // Calculate pending balance from completed orders if Stripe not connected
      // This helps show the crafter how much they're missing out on
      const { data: completedOrders } = await supabase
        .from('orders')
        .select('total_price')
        .eq('crafter_id', user.id)
        .in('status', ['completed', 'shipped', 'delivered']);
      
      if (completedOrders && completedOrders.length > 0) {
        const totalAmount = completedOrders.reduce((sum, o) => sum + (parseFloat(o.total_price) || 0), 0);
        setPendingBalance({
          amount: totalAmount,
          ordersCount: completedOrders.length
        });
      }
    } catch (error) {
      console.error('Error checking Stripe status:', error);
    } finally {
      setStripeLoading(false);
    }
  };

  // Send Stripe reminder if crafter has orders but no Stripe (only once per session)
  const maybeSendStripeReminder = async (hasStripe, hasPendingOrders, orderAmount, orderCount) => {
    if (hasStripe || !hasPendingOrders) return;
    
    // Check if we already sent a reminder today
    const lastReminderKey = `stripe_reminder_${user.id}`;
    const lastReminder = localStorage.getItem(lastReminderKey);
    const today = new Date().toDateString();
    
    if (lastReminder === today) return; // Already sent today
    
    try {
      const response = await fetch(`${API_URL}/api/notifications/stripe-reminder`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          crafter_id: user.id,
          crafter_email: profile?.email || '',
          crafter_name: profile?.full_name || 'Artigiano',
          pending_amount: orderAmount,
          orders_count: orderCount,
          frontend_url: window.location.origin
        })
      });
      
      if (response.ok) {
        localStorage.setItem(lastReminderKey, today);
        console.log('Stripe reminder sent successfully');
      }
    } catch (error) {
      console.error('Failed to send Stripe reminder:', error);
    }
  };

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const { data: productsData } = await supabase
        .from('products').select('*').eq('crafter_id', user.id).order('created_at', { ascending: false });
      if (productsData) setProducts(productsData);

      const { data: portfolioData } = await supabase
        .from('portfolio_items').select('*').eq('crafter_id', user.id).order('created_at', { ascending: false });
      if (portfolioData) setPortfolio(portfolioData);

      if (profile?.categories?.length > 0) {
        const { data: requestsData } = await supabase
          .from('direct_requests')
          .select('*, profiles!direct_requests_client_id_fkey(full_name, avatar_url)')
          .in('category', profile.categories)
          .eq('status', 'open')
          .order('created_at', { ascending: false });
        // Apply 24h filter for free users (done in render via getVisibleRequests)
        if (requestsData) setMatchingRequests(requestsData);
      }

      // Use backend API to get proposals with enriched request data (bypasses RLS)
      try {
        const proposalsResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/crafter/${user.id}/proposals`);
        if (proposalsResponse.ok) {
          const proposalsResult = await proposalsResponse.json();
          setProposals(proposalsResult.proposals || []);
        }
      } catch (err) {
        console.error('Error fetching proposals:', err);
      }
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadOrders = async () => {
    try {
      // Load request-based orders (crafter_id = user.id, order_type = 'request' or null)
      const { data: requestOrders, error: reqError } = await supabase
        .from('orders')
        .select(`*, client:client_id(id, full_name, avatar_url)`)
        .eq('crafter_id', user.id)
        .or('order_type.eq.request,order_type.is.null')
        .order('created_at', { ascending: false });

      if (reqError) console.error('Error loading request orders:', reqError);

      // Load cart-based orders where crafter_id matches (new flow)
      const { data: cartOrders, error: cartError } = await supabase
        .from('orders')
        .select(`*, client:client_id(id, full_name, avatar_url)`)
        .eq('crafter_id', user.id)
        .eq('order_type', 'cart')
        .order('created_at', { ascending: false });

      if (cartError) console.error('Error loading cart orders:', cartError);

      // Allegati caricati dal cliente nella richiesta (flusso richiesta diretta e richieste aperte):
      // vivono in `direct_requests.attachments`, non negli items dell'ordine.
      const requestIds = [...new Set((requestOrders || []).map(o => o.request_id).filter(Boolean))];
      const requestsMap = {};
      if (requestIds.length > 0) {
        const { data: reqRows, error: reqRowsError } = await supabase
          .from('direct_requests')
          .select('id, attachments, description, title, timeline, price_range, request_type')
          .in('id', requestIds);
        if (reqRowsError) console.error('Error loading request attachments:', reqRowsError);
        (reqRows || []).forEach(r => { requestsMap[r.id] = r; });
      }

      const buildCustomizationItem = (order) => {
        const req = requestsMap[order.request_id];
        const baseItem = (order.items && order.items[0]) || {};
        const attachments = { ...(baseItem.customerAttachments || {}) };
        const existingUrls = Object.values(attachments).map(a => (typeof a === 'string' ? a : a?.url));
        (Array.isArray(req?.attachments) ? req.attachments : []).forEach((url, i) => {
          if (url && !existingUrls.includes(url)) {
            let name = url.split('/').pop() || 'allegato';
            try { name = decodeURIComponent(name); } catch (e) { /* keep raw */ }
            attachments[`req-${i}`] = { url, name };
          }
        });
        return { customizationValues: {}, customerAttachments: attachments };
      };

      console.log('Request orders:', requestOrders?.length || 0);
      console.log('Cart orders:', cartOrders?.length || 0);

      // Format cart orders to show crafter's items
      const formattedCartOrders = (cartOrders || []).map(order => ({
        ...order,
        crafterItems: order.items || [],
        isCartOrder: true
      }));

      // Combine and sort all orders (no duplicates since we filter by order_type)
      const allOrders = [
        ...(requestOrders || []).map(o => ({
          ...o,
          isCartOrder: false,
          requestDetails: requestsMap[o.request_id] || null,
          customizationItem: buildCustomizationItem(o),
        })),
        ...formattedCartOrders
      ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

      setOrders(allOrders);
    } catch (error) {
      console.error('Error loading orders:', error);
    }
  };

  const loadReviews = async () => {
    try {
      const { data, error } = await supabase
        .from('reviews')
        .select(`
          *,
          client:client_id(id, full_name, avatar_url),
          product:product_id(id, title, images)
        `)
        .eq('crafter_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading reviews:', error);
        return;
      }
      setReviews(data || []);
    } catch (error) {
      console.error('Error loading reviews:', error);
    }
  };

  // Load users who saved crafter's products as favorites
  const loadProductFans = async () => {
    try {
      // First get all crafter's product IDs
      const { data: crafterProducts, error: productsError } = await supabase
        .from('products')
        .select('id, title, images, base_price')
        .eq('crafter_id', user.id);

      if (productsError) throw productsError;
      if (!crafterProducts || crafterProducts.length === 0) {
        setProductFans([]);
        return;
      }

      const productIds = crafterProducts.map(p => p.id);
      const productsMap = crafterProducts.reduce((acc, p) => {
        acc[p.id] = p;
        return acc;
      }, {});

      // Get favorites for these products (excluding the crafter themselves)
      const { data: favorites, error: favError } = await supabase
        .from('favorites')
        .select('id, user_id, item_id, created_at')
        .eq('item_type', 'product')
        .in('item_id', productIds)
        .neq('user_id', user.id);

      if (favError) throw favError;
      if (!favorites || favorites.length === 0) {
        setProductFans([]);
        return;
      }

      // Get unique user IDs
      const userIds = [...new Set(favorites.map(f => f.user_id))];

      // Fetch user profiles
      const { data: users, error: usersError } = await supabase
        .from('profiles')
        .select('id, full_name, avatar_url, email')
        .in('id', userIds);

      if (usersError) throw usersError;

      const usersMap = (users || []).reduce((acc, u) => {
        acc[u.id] = u;
        return acc;
      }, {});

      // Combine data: group by user, with their saved products
      const fansData = {};
      favorites.forEach(fav => {
        const userId = fav.user_id;
        const product = productsMap[fav.item_id];
        const userProfile = usersMap[userId];

        if (!product || !userProfile) return;

        if (!fansData[userId]) {
          fansData[userId] = {
            user: userProfile,
            savedProducts: [],
            latestSaveDate: fav.created_at
          };
        }

        fansData[userId].savedProducts.push({
          ...product,
          savedAt: fav.created_at
        });

        // Track latest save date
        if (new Date(fav.created_at) > new Date(fansData[userId].latestSaveDate)) {
          fansData[userId].latestSaveDate = fav.created_at;
        }
      });

      // Convert to array and sort by latest save date
      const fansArray = Object.values(fansData).sort(
        (a, b) => new Date(b.latestSaveDate) - new Date(a.latestSaveDate)
      );

      setProductFans(fansArray);
    } catch (error) {
      console.error('Error loading product fans:', error);
      setProductFans([]);
    }
  };

  // Load direct requests addressed to this crafter
  const loadDirectRequests = async () => {
    try {
      const { data, error } = await supabase
        .from('direct_requests')
        .select(`
          *,
          client:client_id(id, full_name, avatar_url, email)
        `)
        .eq('crafter_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading direct requests:', error);
        return;
      }
      setDirectRequests(data || []);
    } catch (error) {
      console.error('Error loading direct requests:', error);
    }
  };

  // Handle crafter response to a direct request
  const handleRespondToDirectRequest = async () => {
    if (!respondingToRequest) return;
    
    if (!requestResponseForm.price || !requestResponseForm.message) {
      toast.error('Inserisci prezzo e messaggio');
      return;
    }

    setSendingRequestResponse(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      
      // Create a proposal for this request via backend API (bypasses RLS)
      const proposalData = {
        request_id: respondingToRequest.id,
        crafter_id: user.id,
        price: parseFloat(requestResponseForm.price),
        timeline: requestResponseForm.timeline || respondingToRequest.timeline || 'Da concordare',
        message: requestResponseForm.message,
        attachments: responseAttachments.map(a => a.url),
        shipping_cost: requestResponseForm.freeShipping ? 0 : (parseFloat(requestResponseForm.shippingCost) || 0)
      };

      const response = await fetch(`${API_URL}/api/proposals/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(proposalData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Errore nella creazione della proposta');
      }

      // Send notification message to client
      // Find or create conversation
      const { data: existingConv } = await supabase
        .from('conversations')
        .select('id')
        .or(`and(participant_1.eq.${user.id},participant_2.eq.${respondingToRequest.client_id}),and(participant_1.eq.${respondingToRequest.client_id},participant_2.eq.${user.id})`)
        .single();

      let convId = existingConv?.id;
      if (!convId) {
        const { data: newConv } = await supabase
          .from('conversations')
          .insert({
            participant_1: user.id,
            participant_2: respondingToRequest.client_id
          })
          .select()
          .single();
        convId = newConv?.id;
      }

      if (convId) {
        const shippingText = requestResponseForm.freeShipping ? '🚚 Spedizione: Gratuita' : `🚚 Spedizione: €${requestResponseForm.shippingCost || '0'}`;
        const notificationMessage = `💡 Proposta per la tua richiesta!\n\nHo preparato una proposta per "${respondingToRequest.title}":\n\n💰 Prezzo: €${requestResponseForm.price}\n${shippingText}\n⏱️ Tempi: ${requestResponseForm.timeline || 'Da concordare'}\n\n${requestResponseForm.message}\n\nVai alla tua Dashboard > Richieste per accettare o rifiutare.`;
        
        await supabase.from('messages').insert({
          conversation_id: convId,
          sender_id: user.id,
          content: notificationMessage,
        });
      }

      toast.success('Proposta inviata al cliente!');
      setRespondingToRequest(null);
      setRequestResponseForm({ price: '', timeline: '', message: '', shippingCost: '', freeShipping: false });
      setResponseAttachments([]);
      loadDirectRequests();
    } catch (error) {
      console.error('Error responding to request:', error);
      toast.error('Errore nell\'invio della proposta');
    } finally {
      setSendingRequestResponse(false);
    }
  };

  // Start a chat with a fan
  const handleStartFanChat = async (fanUserId) => {
    setStartingFanChat(fanUserId);
    try {
      // Check if conversation already exists
      const { data: existingConv } = await supabase
        .from('conversations')
        .select('id')
        .or(`and(participant_1.eq.${user.id},participant_2.eq.${fanUserId}),and(participant_1.eq.${fanUserId},participant_2.eq.${user.id})`)
        .single();

      if (existingConv) {
        navigate(`/messages/${existingConv.id}`);
      } else {
        // Create new conversation
        const { data: newConv, error } = await supabase
          .from('conversations')
          .insert({
            participant_1: user.id,
            participant_2: fanUserId
          })
          .select()
          .single();

        if (error) throw error;
        navigate(`/messages/${newConv.id}`);
      }
    } catch (error) {
      console.error('Error starting chat:', error);
      toast.error('Errore nell\'avvio della chat');
    } finally {
      setStartingFanChat(null);
    }
  };

  // Send discount offer to a fan
  const handleSendDiscountOffer = async () => {
    if (!offerDiscountDialog) return;
    
    const { userId, productId, productTitle } = offerDiscountDialog;
    const discount = parseInt(discountPercentage);
    
    if (isNaN(discount) || discount < 5 || discount > 50) {
      toast.error('Lo sconto deve essere tra 5% e 50%');
      return;
    }

    setSendingOffer(true);
    try {
      // Check if conversation already exists or create one
      let conversationId;
      const { data: existingConv } = await supabase
        .from('conversations')
        .select('id')
        .or(`and(participant_1.eq.${user.id},participant_2.eq.${userId}),and(participant_1.eq.${userId},participant_2.eq.${user.id})`)
        .single();

      if (existingConv) {
        conversationId = existingConv.id;
      } else {
        const { data: newConv, error } = await supabase
          .from('conversations')
          .insert({
            participant_1: user.id,
            participant_2: userId
          })
          .select()
          .single();

        if (error) throw error;
        conversationId = newConv.id;
      }

      // Generate unique discount code
      const timestamp = Date.now().toString(36).toUpperCase();
      const discountCode = `RARIFEX${discount}-${productId.slice(0, 4).toUpperCase()}${timestamp.slice(-4)}`;

      // Save discount code to database
      const { error: codeError } = await supabase
        .from('discount_codes')
        .insert({
          code: discountCode,
          crafter_id: user.id,
          recipient_id: userId,
          product_id: productId,
          discount_percentage: discount,
          message: discountMessage || null
        });

      if (codeError) {
        console.error('Error saving discount code:', codeError);
        // Continue anyway - the code will still be sent via message
      }

      // Compose the offer message
      const offerMessageText = `🎁 **Offerta Speciale per Te!**

Ho notato che hai salvato "${productTitle}" tra i tuoi preferiti!

Ti offro uno sconto esclusivo del **${discount}%** per questo prodotto.

${discountMessage ? `\n💬 "${discountMessage}"\n` : ''}
🏷️ Codice sconto: **${discountCode}**

Inserisci questo codice nel campo "Codice Sconto" al momento del pagamento. Il codice è riservato solo a te e scade tra 30 giorni.`;

      // Send message
      const { error: msgError } = await supabase
        .from('messages')
        .insert({
          conversation_id: conversationId,
          sender_id: user.id,
          content: offerMessageText
        });

      if (msgError) throw msgError;

      // Update conversation last_message_at
      await supabase
        .from('conversations')
        .update({ last_message_at: new Date().toISOString() })
        .eq('id', conversationId);

      // Send email notification to recipient
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      
      // Get recipient email from profile
      const { data: recipientProfile } = await supabase
        .from('profiles')
        .select('email, full_name')
        .eq('id', userId)
        .single();
      
      if (recipientProfile?.email) {
        fetch(`${API_URL}/api/email/discount-received`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            recipient_email: recipientProfile.email,
            recipient_name: recipientProfile.full_name || 'Cliente',
            crafter_name: profile?.full_name || profile?.business_name || 'Artigiano',
            product_title: productTitle,
            discount_percentage: discount,
            discount_code: discountCode,
            personal_message: discountMessage || null
          })
        }).catch(err => console.error('Error sending discount email:', err));
      }

      toast.success('Offerta inviata con successo! 🎉');
      setOfferDiscountDialog(null);
      setDiscountPercentage('10');
      setDiscountMessage('');

      // Navigate to the conversation
      navigate(`/messages/${conversationId}`);
    } catch (error) {
      console.error('Error sending discount offer:', error);
      toast.error('Errore nell\'invio dell\'offerta');
    } finally {
      setSendingOffer(false);
    }
  };

  const handleSendResponse = async (reviewId) => {
    if (!responseText.trim()) {
      toast.error('Scrivi una risposta prima di inviare');
      return;
    }

    setSendingResponse(true);
    try {
      const { error } = await supabase
        .from('reviews')
        .update({
          crafter_response: responseText.trim(),
          crafter_response_at: new Date().toISOString()
        })
        .eq('id', reviewId)
        .eq('crafter_id', user.id);

      if (error) throw error;

      toast.success('Risposta inviata con successo!');
      setRespondingToReview(null);
      setResponseText('');
      loadReviews();
    } catch (error) {
      console.error('Error sending response:', error);
      toast.error('Errore nell\'invio della risposta');
    } finally {
      setSendingResponse(false);
    }
  };

  const loadStorefront = async () => {
    try {
      const { data: freshProfile } = await supabase
        .from('profiles').select('*').eq('id', user.id).single();
      if (freshProfile) {
        setStorefrontTitle(freshProfile.storefront_title || freshProfile.full_name || '');
        setStorefrontMotto(freshProfile.storefront_motto || '');
        setStorefrontSectionName(freshProfile.storefront_section_name || 'La Bottega');
        setStorefrontDescription(freshProfile.bio || '');
        setStorefrontImage(freshProfile.cover_image || '');
        // Advanced fields
        setWorkshopStory(freshProfile.workshop_story || '');
        setMaterials(Array.isArray(freshProfile.materials) ? freshProfile.materials : []);
        setFoundedYear(freshProfile.founded_year || '');
        setPresentationVideo(freshProfile.presentation_video || '');
        setCertifications(freshProfile.certifications || []);
        setCoverImages(freshProfile.cover_images || []);
        // Social links
        setSocialLinks(freshProfile.social_links || {
          website: '',
          instagram: '',
          facebook: '',
          tiktok: '',
          youtube: '',
          pinterest: '',
          linkedin: ''
        });
      }
    } catch (error) {
      console.error('Error loading storefront:', error);
    }
  };

  // Social link validation patterns
  const socialValidationPatterns = {
    instagram: {
      patterns: ['instagram.com', 'instagr.am'],
      name: 'Instagram'
    },
    facebook: {
      patterns: ['facebook.com', 'fb.com', 'fb.me'],
      name: 'Facebook'
    },
    tiktok: {
      patterns: ['tiktok.com'],
      name: 'TikTok'
    },
    youtube: {
      patterns: ['youtube.com', 'youtu.be'],
      name: 'YouTube'
    },
    pinterest: {
      patterns: ['pinterest.com', 'pinterest.it', 'pin.it'],
      name: 'Pinterest'
    },
    linkedin: {
      patterns: ['linkedin.com'],
      name: 'LinkedIn'
    }
  };

  const validateSocialLinks = () => {
    for (const [platform, value] of Object.entries(socialLinks)) {
      if (value && value.trim()) {
        const validation = socialValidationPatterns[platform];
        if (validation) {
          const link = value.toLowerCase().trim();
          const isValid = validation.patterns.some(pattern => link.includes(pattern));
          if (!isValid) {
            return {
              valid: false,
              error: `Il link inserito per ${validation.name} non è valido. Deve contenere "${validation.patterns[0]}"`
            };
          }
        }
      }
    }
    return { valid: true };
  };

  // Salva i contenuti della vetrina (comuni a base e avanzata).
  // Con silent=true non mostra il toast di successo e restituisce true/false
  // (usato dall'editor della vetrina avanzata, che salva anche la grafica).
  const handleUpdateStorefront = async ({ silent = false } = {}) => {
    // Validate social links before saving
    const socialValidation = validateSocialLinks();
    if (!socialValidation.valid) {
      toast.error(socialValidation.error, { duration: 5000 });
      return false;
    }
    
    try {
      // Basic fields that always exist
      const updateData = { 
        storefront_title: storefrontTitle, 
        bio: storefrontDescription, 
        cover_image: storefrontImage 
      };
      
      // First, try to update basic fields via Supabase
      const { error: basicError } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', user.id);
        
      if (basicError) {
        console.error('Basic update error:', basicError);
        throw new Error(basicError.message || 'Errore durante il salvataggio base');
      }
      
      // Update advanced fields via backend API (bypasses RLS and handles missing columns)
      const advancedData = {
        user_id: user.id,
        // Stringa vuota (non null) per poter anche cancellare un valore
        storefront_motto: storefrontMotto.trim(),
        storefront_section_name: storefrontSectionName || 'La Bottega',
        workshop_story: workshopStory.trim(),
        materials,
        founded_year: foundedYear ? parseInt(foundedYear) : null,
        presentation_video: presentationVideo.trim(),
        // Le certificazioni si scelgono nel profilo (elenco Made in Italy): qui non si toccano
        cover_images: coverImages.length > 0 ? coverImages : null,
        social_links: Object.values(socialLinks).some(v => v) ? socialLinks : null
      };
      
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const { data: sessionData } = await supabase.auth.getSession();
        const response = await fetch(`${API_URL}/api/profile/update-advanced`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${sessionData?.session?.access_token}`,
          },
          body: JSON.stringify(advancedData)
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          console.warn('Advanced fields update warning:', errorData);
          
          // Check if it's a missing column error for social_links
          if (errorData.detail && errorData.detail.includes('social_links')) {
            toast.error('Per salvare i social links, devi aggiungere la colonna "social_links" (tipo JSONB) alla tabella profiles in Supabase.', { duration: 8000 });
          } else {
            toast.error(errorData.detail || 'Salvataggio di storia, foto e social non riuscito');
          }
          return false;
        }
        const okData = await response.json().catch(() => ({}));
        if (okData?.warning) toast.warning(okData.warning, { duration: 6000 });
      } catch (advancedError) {
        console.warn('Advanced fields update warning:', advancedError);
        toast.error('Salvataggio di storia, foto e social non riuscito, riprova');
        return false;
      }
      
      clearShopCache();
      if (!silent) toast.success('Vetrina aggiornata con successo!');
      return true;
    } catch (error) {
      console.error('Update storefront error:', error);
      const errorMessage = error?.message || 'Errore sconosciuto';
      toast.error('Errore: ' + errorMessage);
      return false;
    }
  };

  const handleAddProduct = async (e) => {
    e.preventDefault();
    
    // Check product limit for new products (not edits)
    if (!editingProduct) {
      const productCheck = canCreateProduct();
      if (!productCheck.allowed) {
        toast.error(productCheck.message, { duration: 5000 });
        return;
      }
    }
    
    if (!productTitle || !basePrice) {
      toast.error('Compila i campi obbligatori: Titolo e Prezzo');
      return;
    }
    if (baseImages.length === 0) {
      toast.error('Aggiungi almeno un\'immagine');
      return;
    }
    if (!selectedCategory) {
      toast.error('Seleziona una categoria per il prodotto');
      return;
    }
    
    // Check commission terms acceptance for non-immediate products
    if (baseTimeline !== 'immediate' && !commissionTermsAccepted) {
      toast.error('Per i prodotti su commissione, devi accettare i termini relativi ai diritti dell\'acquirente.', { duration: 5000 });
      return;
    }
    
    // Check shipping rule
    if (!selectedShippingRuleId && shippingRules.length === 0) {
      toast.error('Devi creare almeno una regola di spedizione prima di aggiungere prodotti. Vai alla sezione "Spedizioni".', { duration: 5000 });
      return;
    }
    
    // If immediate availability and step 1, go to step 2
    if (baseTimeline === 'immediate' && productFormStep === 1) {
      setProductFormStep(2);
      return;
    }
    
    try {
      const productData = {
        crafter_id: user.id,
        title: productTitle,
        description: productDescription || '',
        base_price: parseFloat(basePrice),
        images: baseImages,
        category: selectedCategory.main, // Use the main category ID for filtering
        status: 'active',
        shipping_rule_id: selectedShippingRuleId || shippingRules.find(r => r.is_default)?.id || null,
        highlights: productHighlights || [], // Punti di Forza
        customization_options: { 
          main_image_index: mainImageIndex, 
          timeline: baseTimeline, 
          hasVariants,
          variants: hasVariants ? variants : [],
          hasCustomizations,
          customizations: hasCustomizations ? customizations : [],
          inventory: baseTimeline === 'immediate' ? inventory : {},
          categoryPath: selectedCategory, // Store full category path
          crafterDiscount: crafterDiscount ? parseFloat(crafterDiscount) : 0, // Sconto artigiano
          commissionTermsAccepted: baseTimeline !== 'immediate' ? commissionTermsAccepted : null // Salva accettazione termini
        }
      };

      if (editingProduct) {
        // Update existing product
        const { error } = await supabase
          .from('products')
          .update(productData)
          .eq('id', editingProduct.id);
        if (error) throw new Error(error.message);
        toast.success('Prodotto aggiornato con successo!');
      } else {
        // Create new product
        const { error } = await supabase.from('products').insert(productData).select();
        if (error) throw new Error(error.message);
        toast.success('Creazione aggiunta con successo!');
      }
      
      // Auto-publish category if it was hidden (makes category visible when it has content)
      if (selectedCategory?.sub || selectedCategory?.subSub) {
        const categorySlug = selectedCategory.subSub || selectedCategory.sub;
        try {
          const publishResponse = await fetch(`${API_URL}/api/categories/auto-publish?category_slug=${categorySlug}`, {
            method: 'POST'
          });
          const publishResult = await publishResponse.json();
          if (publishResult.published_categories?.length > 0) {
            toast.success(`Categoria "${publishResult.published_categories[0]}" pubblicata automaticamente!`, { duration: 3000 });
          }
        } catch (e) {
          console.log('Auto-publish check completed');
        }
      }
      
      setIsProductDialogOpen(false);
      resetProductForm();
      await loadDashboardData();
    } catch (error) {
      toast.error('Errore: ' + error.message);
    }
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setProductTitle(product.title || '');
    setProductDescription(product.description || '');
    setBaseImages(product.images || []);
    setMainImageIndex(product.customization_options?.main_image_index || 0);
    setBasePrice(product.base_price?.toString() || '');
    setCrafterDiscount(product.customization_options?.crafterDiscount?.toString() || '');
    setBaseTimeline(product.customization_options?.timeline || 'immediate');
    setCommissionTermsAccepted(product.customization_options?.commissionTermsAccepted || false);
    setProductCategory(product.category || '');
    setSelectedCategory(product.customization_options?.categoryPath || null);
    setHasVariants(product.customization_options?.hasVariants || false);
    setVariants(product.customization_options?.variants || []);
    setHasCustomizations(product.customization_options?.hasCustomizations || false);
    setCustomizations(product.customization_options?.customizations || []);
    setInventory(product.customization_options?.inventory || {});
    setSelectedShippingRuleId(product.shipping_rule_id || shippingRules?.find(r => r.is_default)?.id || null);
    setProductHighlights(product.highlights || []);
    setProductFormStep(1);
    setIsProductDialogOpen(true);
  };

  const handleOpenNewProduct = () => {
    resetProductForm();
    setEditingProduct(null);
    setProductFormStep(1);
    setIsProductDialogOpen(true);
  };

  const handleAddPortfolio = async (e) => {
    e.preventDefault();
    try {
      const { error } = await supabase.from('portfolio_items').insert({
        crafter_id: user.id,
        title: portfolioTitle,
        description: portfolioDescription,
        image_url: portfolioImage,
        category: portfolioCategory || profile?.categories?.[0] || 'Other',
      });
      if (error) throw error;
      toast.success('Aggiunto al portfolio!');
      setIsPortfolioDialogOpen(false);
      resetPortfolioForm();
      loadDashboardData();
    } catch (error) {
      toast.error('Errore: ' + error.message);
    }
  };

  const handleRespondToRequest = async (requestId, action) => {
    if (action === 'reject' && !rejectReason.trim()) {
      toast.error('Devi specificare un motivo per il rifiuto');
      return;
    }
    
    // Check proposal limit for accept action
    if (action === 'accept') {
      const proposalCheck = canSendProposal();
      if (!proposalCheck.allowed) {
        toast.error(proposalCheck.message, { duration: 5000 });
        return;
      }
    }
    
    try {
      if (action === 'accept') {
        const { error } = await supabase.from('proposals').insert({
          request_id: requestId, crafter_id: user.id, message: responseMessage,
          proposed_price: 0, estimated_timeline: '1-2weeks', status: 'pending',
        });
        if (error) throw error;
        
        // Increment proposal counter
        await incrementProposalCounter();
        
        toast.success('Proposta inviata!');
      } else if (action === 'reject') {
        toast.success('Richiesta rifiutata');
      }
      setResponseMessage('');
      setRejectReason('');
      loadDashboardData();
    } catch (error) {
      toast.error('Errore: ' + error.message);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo prodotto?')) return;
    try {
      const { error } = await supabase.from('products').delete().eq('id', productId);
      if (error) throw error;
      toast.success('Prodotto eliminato');
      loadDashboardData();
    } catch (error) {
      toast.error('Errore: ' + error.message);
    }
  };

  const handleDeletePortfolio = async (itemId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo elemento?')) return;
    try {
      const { error } = await supabase.from('portfolio_items').delete().eq('id', itemId);
      if (error) throw error;
      toast.success('Elemento eliminato');
      loadDashboardData();
    } catch (error) {
      toast.error('Errore: ' + error.message);
    }
  };

  const resetProductForm = () => {
    setEditingProduct(null);
    setProductTitle(''); 
    setProductDescription(''); 
    setBaseImages([]); 
    setMainImageIndex(0);
    setBasePrice('');
    setCrafterDiscount('');
    setBaseTimeline('immediate'); 
    setCommissionTermsAccepted(false);
    setProductCategory(''); 
    setSelectedCategory(null);
    setHasVariants(false);
    setVariants([]);
    setHasCustomizations(false);
    setCustomizations([]);
    setInventory({});
    setProductHighlights([]);
    setProductFormStep(1);
    // Reset to default shipping rule
    const defaultRule = shippingRules?.find(r => r.is_default && r.is_active);
    setSelectedShippingRuleId(defaultRule?.id || null);
  };

  const resetPortfolioForm = () => {
    setPortfolioTitle(''); setPortfolioDescription(''); setPortfolioImage(''); setPortfolioCategory('');
  };

  // Inventory management functions
  const handleChangeAvailability = async (productId, variantKey, newTimeline) => {
    try {
      const product = products.find(p => p.id === productId);
      if (!product) return;
      
      const currentOptions = product.customization_options || {};
      const currentVariantSettings = currentOptions.variantSettings || {};
      
      // Update variant settings
      const updatedVariantSettings = {
        ...currentVariantSettings,
        [variantKey]: {
          ...(currentVariantSettings[variantKey] || {}),
          timeline: newTimeline,
          // If switching away from immediate, zero out stock
          stock: newTimeline === 'immediate' ? (currentVariantSettings[variantKey]?.stock || 0) : 0
        }
      };
      
      // Also update legacy timeline if it's the default/only variant
      const hasVariants = currentOptions.hasVariants && currentOptions.variants?.length > 0;
      
      const updatedOptions = {
        ...currentOptions,
        variantSettings: updatedVariantSettings,
        // Update legacy timeline for backwards compatibility (only for non-variant products)
        ...((!hasVariants && variantKey === 'default') ? { timeline: newTimeline } : {})
      };
      
      const { error } = await supabase
        .from('products')
        .update({ customization_options: updatedOptions })
        .eq('id', productId);
      
      if (error) throw error;
      
      // Update local state
      setProducts(prev => prev.map(p => 
        p.id === productId 
          ? { ...p, customization_options: updatedOptions }
          : p
      ));
      
      if (newTimeline === 'immediate') {
        toast.success('Disponibilità aggiornata. Inserisci la quantità a magazzino.');
      } else {
        toast.success('Impostato su ordinazione');
      }
    } catch (error) {
      console.error('Error changing availability:', error);
      toast.error('Errore nell\'aggiornamento della disponibilità');
    }
  };

  const handleUpdateStock = async (productId, variantKey, quantity) => {
    try {
      const product = products.find(p => p.id === productId);
      if (!product) return;
      
      const currentOptions = product.customization_options || {};
      const currentVariantSettings = currentOptions.variantSettings || {};
      const currentInventory = currentOptions.inventory || {};
      
      // Update variant settings with new stock
      const updatedVariantSettings = {
        ...currentVariantSettings,
        [variantKey]: {
          ...(currentVariantSettings[variantKey] || { timeline: 'immediate' }),
          stock: quantity
        }
      };
      
      // Also update legacy inventory for backwards compatibility
      const updatedInventory = {
        ...currentInventory,
        [variantKey]: quantity
      };
      
      const updatedOptions = {
        ...currentOptions,
        variantSettings: updatedVariantSettings,
        inventory: updatedInventory
      };
      
      const { error } = await supabase
        .from('products')
        .update({ customization_options: updatedOptions })
        .eq('id', productId);
      
      if (error) throw error;
      
      // Update local state
      setProducts(prev => prev.map(p => 
        p.id === productId 
          ? { ...p, customization_options: updatedOptions }
          : p
      ));
    } catch (error) {
      console.error('Error updating stock:', error);
      toast.error('Errore nell\'aggiornamento del magazzino');
    }
  };

  const handleUpdateInventory = async (productId, variantKey, quantity) => {
    // Alias for backwards compatibility
    return handleUpdateStock(productId, variantKey, quantity);
  };

  const handleSwitchToImmediate = async (productId, variantKey) => {
    return handleChangeAvailability(productId, variantKey, 'immediate');
  };

  const handleConvertToImmediate = async (productId) => {
    try {
      const product = products.find(p => p.id === productId);
      if (!product) return;
      
      const currentOptions = product.customization_options || {};
      const hasVariants = currentOptions.hasVariants && currentOptions.variants?.length > 0;
      const variants = currentOptions.variants || [];
      
      // Initialize variant settings for all variants
      let newVariantSettings = {};
      
      if (!hasVariants) {
        newVariantSettings = { 'default': { timeline: 'immediate', stock: 0 } };
      } else {
        // Generate all combinations
        const combinations = variants.reduce((acc, variant) => {
          if (acc.length === 0) {
            return variant.values.map(v => [{ type: variant.type, value: v }]);
          }
          const newCombinations = [];
          acc.forEach(combo => {
            variant.values.forEach(v => {
              newCombinations.push([...combo, { type: variant.type, value: v }]);
            });
          });
          return newCombinations;
        }, []);
        
        combinations.forEach(combo => {
          const key = combo.map(c => `${c.type}:${c.value}`).join('|');
          newVariantSettings[key] = { timeline: 'immediate', stock: 0 };
        });
      }
      
      const updatedOptions = {
        ...currentOptions,
        timeline: 'immediate',
        variantSettings: newVariantSettings
      };
      
      const { error } = await supabase
        .from('products')
        .update({ customization_options: updatedOptions })
        .eq('id', productId);
      
      if (error) throw error;
      
      // Update local state
      setProducts(prev => prev.map(p => 
        p.id === productId 
          ? { ...p, customization_options: updatedOptions }
          : p
      ));
      
      toast.success('Prodotto convertito in disponibilità immediata. Aggiorna le quantità!');
    } catch (error) {
      console.error('Error converting to immediate:', error);
      toast.error('Errore nella conversione');
    }
  };

  // Order management functions
  const activeOrders = orders.filter(o => ['confirmed', 'in_progress', 'ready', 'shipped'].includes(o.status));
  const completedOrders = orders.filter(o => ['delivered', 'completed', 'cancelled'].includes(o.status));

  const getOrderStatusLabel = (status) => {
    switch (status) {
      case 'confirmed': return 'Confermato';
      case 'in_progress': return 'In Lavorazione';
      case 'ready': return 'Pronto';
      case 'shipped': return 'Spedito';
      case 'delivered': return 'Consegnato';
      case 'completed': return 'Completato';
      default: return status;
    }
  };

  const getOrderStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-blue-100 text-blue-700';
      case 'in_progress': return 'bg-amber-100 text-amber-700';
      case 'ready': return 'bg-purple-100 text-purple-700';
      case 'shipped': return 'bg-indigo-100 text-indigo-700';
      case 'delivered': return 'bg-teal-100 text-teal-700';
      case 'completed': return 'bg-green-100 text-green-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  // Convert timeline slug to human-readable Italian label
  const getTimelineLabel = (timeline) => {
    if (!timeline) return '-';
    // Handle custom date format
    if (timeline.startsWith('custom:')) {
      return `Data: ${timeline.split(':')[1]}`;
    }
    // Map technical values to human-readable labels
    const timelineMap = {
      '1week': '1 Settimana',
      '2weeks': '2 Settimane',
      '1month': '1 Mese',
      '2months': '2 Mesi',
      '3months': '3 Mesi',
      '6months': '6 Mesi',
      'flexible': 'Flessibile',
      '1-2weeks': '1-2 Settimane',
      '1-2days': '1-2 Giorni',
      '3-5days': '3-5 Giorni',
      '2-3weeks': '2-3 Settimane',
      '3-4weeks': '3-4 Settimane',
      'over-month': 'Oltre 1 mese',
      'urgent': 'Urgente',
      'custom': 'Personalizzato'
    };
    return timelineMap[timeline] || timeline;
  };

  const handleStartOrder = async (orderId) => {
    try {
      const { error } = await supabase
        .from('orders')
        .update({ status: 'in_progress', started_at: new Date().toISOString() })
        .eq('id', orderId);

      if (error) throw error;
      toast.success('Ordine avviato! Buon lavoro!');
      loadOrders();
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  const handleMarkReady = async (orderId, skipWarning = false) => {
    // Check if order has progress images
    const order = orders.find(o => o.id === orderId);
    const hasProgressImages = order?.progress_images && order.progress_images.length > 0;
    
    if (!hasProgressImages && !skipWarning) {
      // Show warning dialog
      setPendingReadyOrderId(orderId);
      setShowNoImagesWarning(true);
      return;
    }
    
    try {
      const { error } = await supabase
        .from('orders')
        .update({ status: 'ready', ready_at: new Date().toISOString() })
        .eq('id', orderId);

      if (error) throw error;
      toast.success('Ordine pronto per la spedizione!');
      setShowNoImagesWarning(false);
      setPendingReadyOrderId(null);
      loadOrders();
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  // ===== Annullamento ordine (artigiano) =====
  const [cancelOrderTarget, setCancelOrderTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelReliability, setCancelReliability] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  const openCancelDialog = async (order) => {
    setCancelOrderTarget(order);
    setCancelReason('');
    setCancelReliability(null);
    try {
      const res = await fetch(`${API_URL}/api/crafters/${user.id}/reliability`);
      if (res.ok) setCancelReliability(await res.json());
    } catch (e) { /* non bloccante */ }
  };

  const handleContactBuyerFromCancel = async () => {
    const buyerId = cancelOrderTarget?.client_id || cancelOrderTarget?.client?.id;
    if (!buyerId) {
      toast.error('Impossibile aprire la chat con l\'acquirente');
      return;
    }
    try {
      const { data: existingConv } = await supabase
        .from('conversations')
        .select('id')
        .or(`and(participant_1.eq.${user.id},participant_2.eq.${buyerId}),and(participant_1.eq.${buyerId},participant_2.eq.${user.id})`)
        .single();
      if (existingConv) {
        navigate(`/messages/${existingConv.id}`);
      } else {
        const { data: newConv, error } = await supabase
          .from('conversations')
          .insert({ participant_1: user.id, participant_2: buyerId })
          .select()
          .single();
        if (error) throw error;
        navigate(`/messages/${newConv.id}`);
      }
    } catch (e) {
      console.error('Error starting chat with buyer:', e);
      toast.error('Errore nell\'avvio della chat');
    }
  };

  const handleCancelOrderCrafter = async () => {
    if (!cancelOrderTarget) return;
    if (!cancelReason.trim()) {
      toast.error('Il motivo dell\'annullamento è obbligatorio');
      return;
    }
    setCancelling(true);
    try {
      const res = await fetch(`${API_URL}/api/orders/${cancelOrderTarget.id}/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.id, role: 'crafter', reason: cancelReason.trim() })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Errore durante l\'annullamento');
      toast.success('Ordine annullato. Il cliente è stato rimborsato e notificato.');
      setCancelOrderTarget(null);
      loadOrders();
    } catch (e) {
      toast.error(e.message || 'Impossibile annullare l\'ordine');
    } finally {
      setCancelling(false);
    }
  };
  const handleProgressImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    
    const MAX_IMAGES = 5;
    const MAX_SIZE = 5 * 1024 * 1024; // 5MB
    const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp'];
    
    if (progressImages.length + files.length > MAX_IMAGES) {
      toast.error(`Puoi caricare massimo ${MAX_IMAGES} immagini`);
      return;
    }
    
    for (const file of files) {
      const ext = file.name.split('.').pop()?.toLowerCase();
      if (!ALLOWED_EXTENSIONS.includes(ext)) {
        toast.error(`Formato non consentito: ${file.name}`);
        continue;
      }
      
      if (file.size > MAX_SIZE) {
        toast.error(`File troppo grande: ${file.name}. Max 5MB`);
        continue;
      }
      
      setUploadingProgress(true);
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await fetch(`${API_URL}/api/upload/proposal-attachment`, {
          method: 'POST',
          body: formData
        });
        
        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          throw new Error(error.detail || 'Upload fallito');
        }
        
        const result = await response.json();
        setProgressImages(prev => [...prev, {
          url: result.url,
          filename: result.filename
        }]);
        
      } catch (error) {
        console.error('Upload error:', error);
        toast.error(error.message || `Errore nel caricamento`);
      } finally {
        setUploadingProgress(false);
      }
    }
    
    e.target.value = '';
  };
  
  // Remove progress image
  const handleRemoveProgressImage = (index) => {
    setProgressImages(prev => prev.filter((_, i) => i !== index));
  };
  
  // Send progress update to client
  const handleSendProgressUpdate = async (order) => {
    if (progressImages.length === 0) {
      toast.error('Carica almeno un\'immagine');
      return;
    }
    
    setSendingProgress(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const orderTitle = order.isCartOrder 
        ? (order.crafterItems?.[0]?.title || 'Ordine')
        : order.title;
      
      const response = await fetch(`${API_URL}/api/orders/progress-update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          order_id: order.id,
          crafter_id: user?.id,
          crafter_name: profile?.storefront_title || profile?.full_name || 'Artigiano',
          client_id: order.client_id || order.client?.id,
          image_urls: progressImages.map(img => img.url),
          message: progressMessage,
          order_title: orderTitle,
          frontend_url: window.location.origin
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        // If "Add to Portfolio" is checked, save images to portfolio
        if (addToPortfolio) {
          for (const img of progressImages) {
            const { error: portfolioError } = await supabase
              .from('portfolio_items')
              .insert({
                crafter_id: user?.id,
                title: `Lavorazione: ${orderTitle}`,
                description: progressMessage || `Foto dalla lavorazione dell'ordine "${orderTitle}"`,
                image_url: img.url,
                category: profile?.categories?.[0] || 'Artigianato'
              });
            
            if (portfolioError) {
              console.error('Error adding to portfolio:', portfolioError);
            }
          }
          toast.success('Aggiornamento inviato e aggiunto al portfolio!');
        } else {
          toast.success('Aggiornamento inviato al cliente!');
        }
        
        setProgressImages([]);
        setProgressMessage('');
        setAddToPortfolio(false);
        loadOrders(); // Reload to show updated progress_images
      } else {
        throw new Error(result.error || 'Errore nell\'invio');
      }
    } catch (error) {
      console.error('Send progress error:', error);
      toast.error(error.message || 'Errore nell\'invio dell\'aggiornamento');
    } finally {
      setSendingProgress(false);
    }
  };

  const [shippingDialogOpen, setShippingDialogOpen] = useState(false);
  const [selectedOrderForShipping, setSelectedOrderForShipping] = useState(null);

  const handleOpenShippingDialog = (order) => {
    setSelectedOrderForShipping(order);
    setShippingDialogOpen(true);
  };

  // Chiamato dopo che il ShipmentConfirmDialog ha creato lo Shipment via backend.
  // Qui aggiorniamo lo stato dell'ordine (compat) e mandiamo email/chat al cliente.
  const handleShipmentCreated = async (shipment) => {
    if (!selectedOrderForShipping) return;
    try {
      // 1) Aggiorna orders.status='shipped' + campi compat (letti da UI esistenti)
      await supabase
        .from('orders')
        .update({
          status: 'shipped',
          shipped_at: new Date().toISOString(),
          tracking_number: shipment.tracking_number || null,
          shipping_carrier: shipment.carrier_name || null,
        })
        .eq('id', selectedOrderForShipping.id);

      const API_URL = process.env.REACT_APP_BACKEND_URL;

      // 2) Info cliente
      const { data: clientProfile } = await supabase
        .from('profiles')
        .select('email, full_name')
        .eq('id', selectedOrderForShipping.client_id)
        .single();

      // 3) Email notifica
      if (clientProfile?.email) {
        fetch(`${API_URL}/api/email/order-shipped`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            client_email: clientProfile.email,
            client_name: clientProfile.full_name || 'Cliente',
            order_number: selectedOrderForShipping.order_number,
            tracking_number: shipment.tracking_number || null,
            carrier: shipment.carrier_name || null,
          }),
        }).catch((err) => console.error('Error sending shipped email:', err));
      }

      // 4) Messaggio in chat
      const orderTitle = selectedOrderForShipping.isCartOrder
        ? (selectedOrderForShipping.crafterItems?.[0]?.title || 'Ordine')
        : selectedOrderForShipping.title;

      fetch(`${API_URL}/api/notifications/order-shipped`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          order_id: selectedOrderForShipping.id,
          crafter_id: user?.id,
          crafter_name: profile?.storefront_title || profile?.full_name || 'Artigiano',
          client_id: selectedOrderForShipping.client_id,
          order_number: selectedOrderForShipping.order_number || `ORD-${selectedOrderForShipping.id?.slice(0, 8).toUpperCase()}`,
          order_title: orderTitle,
          tracking_number: shipment.tracking_number || null,
          carrier: shipment.carrier_name || null,
          frontend_url: window.location.origin,
        }),
      }).catch((err) => console.error('Error sending shipped message:', err));

      // 5) Refresh UI ordini
      if (typeof loadOrders === 'function') loadOrders();
    } catch (e) {
      console.error('Post-shipment update error:', e);
      toast.error('Spedizione creata ma aggiornamento ordine fallito');
    }
  };

  const handleMarkShipped = async () => {
    // DEPRECATO — sostituito da ShipmentConfirmDialog + handleShipmentCreated.
    // Mantenuto solo per non rompere eventuali riferimenti residui.
    setShippingDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#DAA520] mx-auto"></div>
      </div>
    );
  }

  // Determine crafter type and tier
  const crafterType = profile?.crafter_type || 'hobbista';
  const tier = profile?.tier || 'free';
  const isHobbista = crafterType === 'hobbista';
  const isPro = isPremiumUser; // Use the global premium check
  const productsCount = products.length;
  const proposalsThisWeek = profile?.proposals_this_week || 0;
  const maxProposals = isPro ? '∞' : MAX_PROPOSALS_FREE_WEEK;

  return (
    <div data-testid="crafter-dashboard-content">
      {/* Stripe Registration Sticky Banner - Show if not connected. Unico banner
          Stripe della dashboard: visibile su tutte le sezioni (sticky top).
          Nascosto nella sezione Pagamenti (stripe-setup) dove sarebbe ridondante. */}
      {!stripeConnected && !stripeLoading && viewMode !== 'stripe-setup' && (
        <div className={`sticky top-0 z-40 mb-4 rounded-lg shadow-md transition-all ${
          pendingBalance.ordersCount > 0 
            ? 'bg-gradient-to-r from-amber-500 to-orange-500' 
            : 'bg-gradient-to-r from-red-500 via-orange-500 to-amber-500'
        }`}>
          <div className="px-4 py-3 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="p-2 rounded-full bg-white/20 flex-shrink-0">
                {pendingBalance.ordersCount > 0 ? (
                  <AlertCircle className="w-5 h-5 text-white" />
                ) : (
                  <CreditCard className="w-5 h-5 text-white" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                {pendingBalance.ordersCount > 0 ? (
                  <>
                    <p className="font-semibold text-white text-sm md:text-base">
                      Hai {pendingBalance.ordersCount} ordin{pendingBalance.ordersCount === 1 ? 'e' : 'i'} da incassare (€{pendingBalance.amount.toFixed(2)})
                    </p>
                    <p className="text-white/90 text-xs md:text-sm truncate">
                      Attiva Stripe per ricevere i pagamenti sul tuo conto
                    </p>
                  </>
                ) : (
                  <>
                    <p className="font-semibold text-white text-sm md:text-base">
                      Un ultimo passo per iniziare a vendere
                    </p>
                    <p className="text-white/90 text-xs md:text-sm">
                      Attiva Stripe in pochi minuti e i tuoi prodotti saranno subito acquistabili.
                      I pagamenti arrivano direttamente sul tuo conto, senza passare da Rarifex.
                    </p>
                  </>
                )}
              </div>
            </div>
            <Button 
              size="sm"
              className={`flex-shrink-0 ${
                pendingBalance.ordersCount > 0 
                  ? 'bg-white text-amber-600 hover:bg-white/90' 
                  : 'bg-white text-red-600 hover:bg-white/90 font-semibold'
              }`}
              onClick={() => navigate('/dashboard?tab=artigiano&section=stripe-setup')}
            >
              <CreditCard className="w-4 h-4 mr-1.5" />
              Attiva ora
            </Button>
          </div>
        </div>
      )}

      {/* Tier & Type Status Section - hide in viewMode */}
      {!viewMode && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
          {/* Current Plan Card */}
          <Card className={`p-4 border-2 ${isPro ? 'border-[#DAA520] bg-gradient-to-br from-[#DAA520]/10 to-[#DAA520]/5' : 'border-slate-200'}`}>
            <div className="flex items-center gap-3 mb-3">
              <div className={`p-2 rounded-lg ${isPro ? 'bg-[#DAA520]' : 'bg-slate-200'}`}>
                <Crown className={`w-5 h-5 ${isPro ? 'text-white' : 'text-slate-600'}`} />
              </div>
              <div>
                <p className="font-bold text-lg">
                  {isPro ? 'Premium' : 'Piano Free'}
                </p>
                <p className="text-sm text-slate-500">
                  {isPro ? '€10/mese' : 'Gratuito'}
                </p>
              </div>
            </div>
            
            {/* Badge tipo */}
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              {isHobbista ? (
                <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200">
                  🟢 Hobbista Italia
                </Badge>
              ) : (
              <>
                <Badge className="bg-red-100 text-red-700 border border-red-200">
                  🔴 Artigiano P.IVA
                </Badge>
                {profile?.partita_iva && (
                  <Badge className="bg-green-100 text-green-700 border border-green-200">
                    <Check className="w-3 h-3 mr-1" /> Verificato
                  </Badge>
                )}
              </>
            )}
            {isPro && (
              <Badge className="bg-[#DAA520] text-white">
                <Sparkles className="w-3 h-3 mr-1" /> Creator Plus
              </Badge>
            )}
          </div>

          {/* Limits - Only show proposals for free users */}
          {!isPro && (
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-600">Risposte questa settimana</span>
                <span className={`font-medium ${proposalsThisWeek >= MAX_PROPOSALS_FREE_WEEK ? 'text-red-500' : ''}`}>
                  {proposalsThisWeek}/{maxProposals}
                </span>
              </div>
              <p className="text-xs text-slate-500">
                Prodotti: illimitati
              </p>
            </div>
          )}
          
          {isPro && (
            <div className="text-sm text-slate-600">
              <p className="flex items-center gap-1">
                <Check className="w-4 h-4 text-emerald-500" />
                Tutti i vantaggi Premium attivi
              </p>
            </div>
          )}
        </Card>

        {/* Upgrade to Premium Card */}
        {!isPro && (
          <Card className="p-4 border-2 border-dashed border-[#DAA520]/50 bg-[#DAA520]/5">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-5 h-5 text-[#DAA520]" />
              <p className="font-bold">Passa a Premium</p>
            </div>
            <ul className="space-y-2 text-sm mb-4">
              <li className="flex items-center gap-2 text-slate-700">
                <Check className="w-4 h-4 text-amber-500" />
                Risposte illimitate
              </li>
              <li className="flex items-center gap-2 text-slate-700">
                <Check className="w-4 h-4 text-amber-500" />
                Vedi le richieste subito (no 24h)
              </li>
              <li className="flex items-center gap-2 text-slate-700">
                <Check className="w-4 h-4 text-amber-500" />
                Badge "Creator Plus"
              </li>
              <li className="flex items-center gap-2 text-slate-700">
                <Check className="w-4 h-4 text-amber-500" />
                Boost visibilità
              </li>
              <li className="flex items-center gap-2 text-slate-700">
                <Check className="w-4 h-4 text-amber-500" />
                Analytics avanzate
              </li>
            </ul>
            <Button 
              className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
              onClick={() => window.location.href = '/plans'}
            >
              Attiva Premium - €10/mese
            </Button>
          </Card>
        )}

        {/* Upgrade to Artigiano Card (only for Hobbista) */}
        {isHobbista && (
          <Card className="p-4 border-2 border-dashed border-red-300 bg-red-50/50">
            <div className="flex items-center gap-2 mb-3">
              <div className="p-1.5 rounded-lg bg-red-100">
                <FileText className="w-4 h-4 text-red-600" />
              </div>
              <p className="font-bold">Diventa Artigiano</p>
            </div>
            <p className="text-sm text-slate-600 mb-3">
              Hai la P.IVA? Diventa Artigiano Professionista per ottenere il badge verificato e maggiore credibilità.
            </p>
            <ul className="space-y-2 text-sm mb-4">
              <li className="flex items-center gap-2 text-slate-700">
                <Check className="w-4 h-4 text-red-500" />
                Badge "P.IVA Verificato"
              </li>
              <li className="flex items-center gap-2 text-slate-700">
                <Check className="w-4 h-4 text-red-500" />
                Maggiore fiducia dai clienti
              </li>
            </ul>
            <UpgradeToArtigianoDialog profile={profile} onSuccess={() => window.location.reload()} />
          </Card>
        )}

        {/* Premium Benefits Card (shown when already Premium) */}
        {isPro && (
          <Card className="p-4 col-span-1 lg:col-span-2 bg-gradient-to-r from-[#DAA520]/10 to-transparent border-[#DAA520]/30">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-5 h-5 text-[#DAA520]" />
              <p className="font-bold">I tuoi vantaggi Premium</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div className="flex items-center gap-2 text-sm">
                <Check className="w-4 h-4 text-amber-500" />
                <span>Risposte illimitate</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Check className="w-4 h-4 text-amber-500" />
                <span>Richieste immediate</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Check className="w-4 h-4 text-amber-500" />
                <span>Badge Creator Plus</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Check className="w-4 h-4 text-amber-500" />
                <span>Boost visibilità</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Check className="w-4 h-4 text-amber-500" />
                <span>Analytics avanzate</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Check className="w-4 h-4 text-amber-500" />
                <span>Ranking migliorato</span>
              </div>
            </div>
          </Card>
        )}
      </div>
      )}

      {/* Navigation Menu - Custom implementation without Radix Tabs */}
      {!viewMode && (
        <div className="mb-6 flex flex-wrap gap-2 p-1 bg-slate-100 rounded-lg" data-testid="crafter-tabs">
          {[
            { value: 'storefront', icon: Store, label: 'Vetrina' },
            { value: 'orders', icon: Package, label: `Ordini (${activeOrders.length})` },
            { value: 'products', icon: Package, label: 'Prodotti e creazioni' },
            { value: 'inventory', icon: Warehouse, label: 'Magazzino' },
            { value: 'shipping', icon: Truck, label: 'Spedizioni', testId: 'crafter-shipping-tab' },
            { value: 'fans', icon: Heart, label: `Interessati${productFans.length > 0 ? ` (${productFans.length})` : ''}`, testId: 'crafter-fans-tab' },
            { value: 'proposals', icon: FileText, label: 'Proposte' },
            { value: 'direct-requests', icon: Inbox, label: `Richieste Dirette${directRequests.length > 0 ? ` (${directRequests.filter(r => r.status === 'open').length})` : ''}`, testId: 'crafter-direct-requests-tab' },
            { value: 'feedback', icon: Star, label: `Feedback (${reviews.length})` },
          ].map(({ value, icon: Icon, label, testId }) => (
            <button
              key={value}
              data-testid={testId}
              onClick={() => handleMainTabChange(value)}
              className={`inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                mainTab === value
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-white/50'
              }`}
            >
              <Icon className="w-4 h-4 mr-1.5" />
              {label}
            </button>
          ))}
        </div>
      )}

      {/* Content Sections - Simple conditional rendering */}
      {/* VETRINA */}
      {mainTab === 'storefront' && (() => {
        // Contenuti della vetrina: condivisi tra vetrina base e vetrina avanzata
        const storefrontContentFields = (
          <div className="space-y-6">
              {/* Basic Storefront Card */}
              <Card className="p-6">
                <h3 className="text-lg font-bold mb-4">Nome e descrizione</h3>
                <div className="space-y-4">
                  <div>
                    <Label>Nome Vetrina *</Label>
                    <Input value={storefrontTitle} onChange={(e) => setStorefrontTitle(e.target.value)} placeholder="Es: Creazioni Artigianali" />
                  </div>
                  <div>
                    <Label>Motto o Slogan</Label>
                    <Input 
                      value={storefrontMotto} 
                      onChange={(e) => setStorefrontMotto(e.target.value.slice(0, 100))} 
                      placeholder="Es: La tradizione incontra l'innovazione"
                      maxLength={100}
                    />
                    <p className="text-xs text-slate-500 mt-1">{storefrontMotto.length}/100 caratteri - Apparirà come titolo della sezione introduttiva</p>
                  </div>
                  <div>
                    <Label>Come chiami la tua attività?</Label>
                    <select 
                      value={storefrontSectionName} 
                      onChange={(e) => setStorefrontSectionName(e.target.value)}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    >
                      <option value="La Bottega">La Bottega</option>
                      <option value="Il Laboratorio">Il Laboratorio</option>
                      <option value="Il Negozio">Il Negozio</option>
                      <option value="L'Atelier">L'Atelier</option>
                      <option value="Lo Studio">Lo Studio</option>
                      <option value="Il Mio Mondo">Il Mio Mondo</option>
                      <option value="Il Nostro Mondo">Il Nostro Mondo</option>
                      <option value="Chi Siamo">Chi Siamo</option>
                    </select>
                    <p className="text-xs text-slate-500 mt-1">Questo termine verrà usato nel menu della tua vetrina</p>
                  </div>
                  <div>
                    <Label>Descrizione (Chi Sono)</Label>
                    <Textarea value={storefrontDescription} onChange={(e) => setStorefrontDescription(e.target.value)} placeholder="Racconta la tua storia..." rows={4} />
                  </div>
                  <ImageUploader label="Foto Copertina" currentImage={storefrontImage} onImageUploaded={(url) => setStorefrontImage(url)} bucketName="storefronts" />
                </div>
              </Card>

              {/* Storia, foto e social - per tutti (i social richiedono Pro o lancio) */}
              <Card className="p-6 border border-slate-200">
                <div className="flex items-center gap-2 mb-4">
                  <History className="w-5 h-5 text-[#008080]" />
                  <h3 className="text-lg font-bold">Storia, foto e social</h3>
                </div>
                <div className="space-y-4">
                  {/* Workshop Story */}
                  <div>
                    <Label className="flex items-center gap-2">
                      <History className="w-4 h-4 text-[#008080]" />
                      Storia del Laboratorio
                    </Label>
                    <Textarea 
                      value={workshopStory} 
                      onChange={(e) => setWorkshopStory(e.target.value)} 
                      placeholder="Racconta la storia del tuo laboratorio, come è nato, la tua passione..."
                      rows={4}
                    />
                  </div>
                  
                  {/* Founded Year */}
                  <div>
                    <Label className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-[#008080]" />
                      Anno di Fondazione
                    </Label>
                    <Input 
                      type="number" 
                      value={foundedYear} 
                      onChange={(e) => setFoundedYear(e.target.value)} 
                      placeholder="Es: 1985"
                      min="1900"
                      max={new Date().getFullYear()}
                    />
                  </div>
                  
                  {/* Video di presentazione: YouTube o Vimeo, in qualsiasi formato di link */}
                  <div>
                    <Label className="flex items-center gap-2">
                      <Play className="w-4 h-4 text-[#008080]" />
                      Video di presentazione
                    </Label>
                    <Input 
                      value={presentationVideo} 
                      onChange={(e) => setPresentationVideo(e.target.value)} 
                      placeholder="Incolla il link di YouTube o Vimeo"
                      data-testid="storefront-video-input"
                    />
                    {presentationVideo.trim() && !parseVideo(presentationVideo) ? (
                      <p className="text-xs text-red-600 mt-1" data-testid="storefront-video-invalid">
                        Link non riconosciuto: usa il link di un video YouTube (anche Shorts) o Vimeo.
                      </p>
                    ) : parseVideo(presentationVideo) ? (
                      <div className="mt-2 flex items-center gap-3" data-testid="storefront-video-ok">
                        <img src={parseVideo(presentationVideo).thumbnail} alt="" className="w-24 aspect-video object-cover rounded-md bg-slate-100" />
                        <p className="text-xs text-emerald-700">Video {parseVideo(presentationVideo).provider === 'youtube' ? 'YouTube' : 'Vimeo'} riconosciuto: comparirà nella vetrina e nella pagina della bottega.</p>
                      </div>
                    ) : (
                      <p className="text-xs text-slate-500 mt-1">Un breve video del laboratorio o di come lavori: è il modo più diretto per farti conoscere.</p>
                    )}
                  </div>


                  {/* Materiali e tecniche: etichette brevi mostrate nella vetrina e nel negozio */}
                  <div data-testid="storefront-materials">
                    <Label className="flex items-center gap-2">
                      <Award className="w-4 h-4 text-[#008080]" />
                      Materiali e tecniche
                    </Label>
                    <p className="text-xs text-slate-500 mt-0.5 mb-2">Con cosa e come lavori: aiuta i clienti a capire subito il tuo stile (max 12).</p>
                    <div className="flex gap-2">
                      <Input
                        value={newMaterial}
                        maxLength={30}
                        onChange={(e) => setNewMaterial(e.target.value)}
                        onKeyDown={(e) => {
                          if ((e.key === 'Enter' || e.key === ',') && newMaterial.trim()) {
                            e.preventDefault();
                            const t = newMaterial.trim();
                            if (materials.length < 12 && !materials.some((m) => m.toLowerCase() === t.toLowerCase())) setMaterials([...materials, t]);
                            setNewMaterial('');
                          }
                        }}
                        placeholder="Es. Gres, Tornio, Smalti senza piombo"
                        data-testid="storefront-material-input"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        disabled={!newMaterial.trim() || materials.length >= 12}
                        onClick={() => {
                          const t = newMaterial.trim();
                          if (!materials.some((m) => m.toLowerCase() === t.toLowerCase())) setMaterials([...materials, t]);
                          setNewMaterial('');
                        }}
                      >
                        Aggiungi
                      </Button>
                    </div>
                    {materials.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {materials.map((m) => (
                          <Badge key={m} variant="outline" className="bg-white flex items-center gap-1 pr-1">
                            {m}
                            <button type="button" onClick={() => setMaterials(materials.filter((x) => x !== m))} className="p-0.5 rounded hover:bg-red-50" aria-label={`Rimuovi ${m}`}>
                              <X className="w-3 h-3 text-slate-400 hover:text-red-500" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    )}
                    {materials.length < 12 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {MATERIAL_SUGGESTIONS.filter((m) => !materials.some((x) => x.toLowerCase() === m.toLowerCase())).slice(0, 14).map((m) => (
                          <button key={m} type="button" onClick={() => setMaterials([...materials, m])} className="text-xs px-2.5 py-1 rounded-full border border-dashed border-slate-300 text-slate-500 hover:border-[#008080] hover:text-[#008080]">
                            + {m}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Social Links - PRO ONLY */}
                  <div className="pt-4 border-t">
                    <div className="flex items-center justify-between mb-3">
                      <Label className="flex items-center gap-2">
                        <ExternalLink className="w-4 h-4 text-[#008080]" />
                        Link Social e Sito Web
                      </Label>
                      {!canEditSocial && (
                        <Badge className="bg-gradient-to-r from-amber-400 to-amber-500 text-white text-xs">
                          <Crown className="w-3 h-3 mr-1" />
                          Premium
                        </Badge>
                      )}
                    </div>
                    
                    {canEditSocial ? (
                      <>
                        <p className="text-xs text-slate-500 mb-3">
                          Inserisci i link ai tuoi profili social e sito web. Appariranno nella tua vetrina pubblica.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {/* Instagram */}
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-gradient-to-br from-purple-500 to-pink-500">
                              <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                              </svg>
                            </div>
                            <Input 
                              value={socialLinks.instagram} 
                              onChange={(e) => setSocialLinks({...socialLinks, instagram: e.target.value})}
                              placeholder="instagram.com/tuoprofilo"
                              className="flex-1"
                            />
                          </div>
                          {/* Facebook */}
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-blue-600">
                              <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                              </svg>
                            </div>
                            <Input 
                              value={socialLinks.facebook} 
                              onChange={(e) => setSocialLinks({...socialLinks, facebook: e.target.value})}
                              placeholder="facebook.com/tuapagina"
                              className="flex-1"
                            />
                          </div>
                          {/* TikTok */}
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-black">
                              <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
                              </svg>
                            </div>
                            <Input 
                              value={socialLinks.tiktok} 
                              onChange={(e) => setSocialLinks({...socialLinks, tiktok: e.target.value})}
                              placeholder="tiktok.com/@tuoprofilo"
                              className="flex-1"
                            />
                          </div>
                          {/* YouTube */}
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-600">
                              <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                              </svg>
                            </div>
                            <Input 
                              value={socialLinks.youtube} 
                              onChange={(e) => setSocialLinks({...socialLinks, youtube: e.target.value})}
                              placeholder="youtube.com/@tuocanale"
                              className="flex-1"
                            />
                          </div>
                          {/* Pinterest */}
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-500">
                              <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.401.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.354-.629-2.758-1.379l-.749 2.848c-.269 1.045-1.004 2.352-1.498 3.146 1.123.345 2.306.535 3.55.535 6.607 0 11.985-5.365 11.985-11.987C23.97 5.39 18.592.026 11.985.026L12.017 0z"/>
                              </svg>
                            </div>
                            <Input 
                              value={socialLinks.pinterest} 
                              onChange={(e) => setSocialLinks({...socialLinks, pinterest: e.target.value})}
                              placeholder="pinterest.com/tuoprofilo"
                              className="flex-1"
                            />
                          </div>
                          {/* LinkedIn */}
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-blue-700">
                              <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                              </svg>
                            </div>
                            <Input 
                              value={socialLinks.linkedin} 
                              onChange={(e) => setSocialLinks({...socialLinks, linkedin: e.target.value})}
                              placeholder="linkedin.com/in/tuoprofilo"
                              className="flex-1"
                            />
                          </div>
                          {/* Website - Last */}
                          <div className="flex items-center gap-2 md:col-span-2">
                            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#008080]">
                              <Globe className="w-4 h-4 text-white" />
                            </div>
                            <Input 
                              value={socialLinks.website} 
                              onChange={(e) => setSocialLinks({...socialLinks, website: e.target.value})}
                              placeholder="https://www.tuosito.it"
                              className="flex-1"
                            />
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-amber-500 flex items-center justify-center flex-shrink-0">
                            <Crown className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-amber-900 mb-1">Funzionalità Premium</h4>
                            <p className="text-sm text-amber-700 mb-3">
                              Aggiungi il tuo sito web e i link ai tuoi profili social per aumentare la visibilità e permettere ai clienti di seguirti su tutte le piattaforme.
                            </p>
                            <Button 
                              size="sm" 
                              className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white"
                              onClick={() => window.location.href = '/plans'}
                            >
                              <Crown className="w-4 h-4 mr-1" />
                              Passa a Premium
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Multiple Cover Images */}
                  <div>
                    <MultiImageUploader
                      images={coverImages}
                      setImages={setCoverImages}
                      maxImages={5}
                      label="Galleria Copertina"
                      bucketName="storefronts"
                      showMainSelector={false}
                    />
                  </div>
                </div>
              </Card>

          </div>
        );
        return (
        <div className="mt-4">
          {isHobbista && (
            <Card className="p-4 mb-6 border-2 border-dashed border-red-300 bg-red-50/50" data-testid="storefront-upgrade-artigiano-card">
              <div className="flex items-center gap-2 mb-2">
                <div className="p-1.5 rounded-lg bg-red-100">
                  <FileText className="w-4 h-4 text-red-600" />
                </div>
                <p className="font-bold">Hai la Partita IVA?</p>
              </div>
              <p className="text-sm text-slate-600 mb-4">
                Diventa Artigiano Professionista per ottenere il badge "P.IVA Verificato" e maggiore credibilità. Scelta irreversibile.
              </p>
              <UpgradeToArtigianoDialog profile={profile} onSuccess={() => window.location.reload()} />
            </Card>
          )}
          {canCustomizeStorefront && (
            <StorefrontModeSwitch
              mode={storefrontMode}
              liveMode={storefrontLiveMode}
              accessReason={myShop.shop?.accessReason}
              onSelect={handleSelectStorefrontMode}
              busy={switchingStorefront}
            />
          )}

          {storefrontMode === 'advanced' && canCustomizeStorefront && (
            <div className="mt-4 inline-flex rounded-xl border bg-white p-1 shadow-sm" role="tablist" aria-label="Vetrina avanzata" data-testid="advanced-view-switch">
              {[['editor', 'Personalizza'], ['stats', 'Statistiche']].map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  aria-selected={advancedView === id}
                  onClick={() => setAdvancedView(id)}
                  className={`px-4 h-9 rounded-lg text-sm font-medium transition-colors ${advancedView === id ? 'bg-[#008080] text-white shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
                  data-testid={`advanced-view-${id}`}
                >
                  {label}
                </button>
              ))}
            </div>
          )}

          {storefrontMode === 'advanced' && canCustomizeStorefront && advancedView === 'stats' ? (
            <ShopStats isPublished={!!myShop.shop?.is_published} slug={profile?.slug} />
          ) : storefrontMode === 'advanced' && canCustomizeStorefront ? (
            <ShopCustomizer
              shop={myShop.shop}
              onSaved={myShop.applySaved}
              onSaveContents={() => handleUpdateStorefront({ silent: true })}
              contentFields={storefrontContentFields}
              userId={user?.id}
              slug={profile?.slug}
              storefrontTitle={storefrontTitle}
              storefrontMotto={storefrontMotto}
              coverImage={storefrontImage}
              sectionName={storefrontSectionName}
              products={products}
            />
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="storefront-base-editor">
              <div className="space-y-6">
                {storefrontContentFields}
                <Button onClick={() => handleUpdateStorefront()} className="w-full bg-[#DAA520] text-white hover:bg-[#B8860B]" data-testid="storefront-base-save-btn">
                  Salva vetrina base
                </Button>
              </div>

            {/* Preview */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Eye className="w-4 h-4" /><span>Anteprima</span>
              </div>
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-white rounded-xl shadow-lg overflow-hidden border">
                <div className="relative h-40 bg-gradient-to-br from-[#DAA520]/20 to-[#B8860B]/20">
                  {storefrontImage ? (
                    <img src={storefrontImage} alt="Cover" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-400">
                      <ImageIcon className="w-10 h-10 opacity-50" />
                    </div>
                  )}
                  {isPro && !isHobbista && (
                    <div className="absolute top-2 right-2">
                      <Badge className="bg-[#DAA520] text-white text-xs">PRO</Badge>
                    </div>
                  )}
                  <div className="absolute -bottom-6 left-4">
                    <div className="w-12 h-12 rounded-full border-4 border-white bg-[#DAA520] flex items-center justify-center shadow-lg">
                      <span className="text-white font-bold">{(storefrontTitle || 'A')[0].toUpperCase()}</span>
                    </div>
                  </div>
                </div>
                <div className="pt-10 pb-4 px-4">
                  <h2 className="font-bold text-lg">{storefrontTitle || 'Nome Vetrina'}</h2>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="outline" className="text-xs">{profile?.categories?.[0] || 'Categoria'}</Badge>
                    {isHobbista ? (
                      <Badge className="bg-emerald-100 text-emerald-700 text-xs">🟢 Hobbista</Badge>
                    ) : (
                      <Badge className="bg-red-100 text-red-700 text-xs">🔴 Artigiano</Badge>
                    )}
                  </div>
                  <p className="text-slate-600 text-sm mt-2 line-clamp-2">{storefrontDescription || 'La tua descrizione...'}</p>
                  <div className="mt-4 pt-3 border-t grid grid-cols-3 gap-2 text-center text-xs">
                    <div><p className="font-bold text-[#DAA520]">{products.length}</p><p className="text-slate-500">Creazioni</p></div>
                    <div><p className="font-bold text-[#DAA520]">{portfolio.length}</p><p className="text-slate-500">Portfolio</p></div>
                    <div><p className="font-bold text-[#DAA520]">★5.0</p><p className="text-slate-500">Rating</p></div>
                  </div>
                </div>
              </motion.div>
            </div>
            </div>
          )}

          {!myShop.loading && !canCustomizeStorefront && <AdvancedStorefrontTeaser />}
        </div>
        );
      })()}

      {/* ORDINI */}
      {mainTab === 'orders' && (
        <div className="mt-4">
          
          {/* Sub-navigation pills for order sections */}
          <div className="flex gap-2 mb-6">
            <button
              onClick={() => setOrdersView('active')}
              data-testid="crafter-orders-tab-active"
              className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all ${
                ordersView === 'active'
                  ? 'bg-[#DAA520] text-white shadow-lg shadow-[#DAA520]/25'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              <Package className="w-4 h-4" />
              Da Gestire
              {activeOrders.length > 0 && (
                <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${
                  ordersView === 'active' ? 'bg-white/20' : 'bg-[#DAA520] text-white'
                }`}>
                  {activeOrders.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setOrdersView('completed')}
              data-testid="crafter-orders-tab-completed"
              className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all ${
                ordersView === 'completed'
                  ? 'bg-[#10B981] text-white shadow-lg shadow-[#10B981]/25'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              <CheckCircle2 className="w-4 h-4" />
              Completati
              {completedOrders.length > 0 && (
                <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${
                  ordersView === 'completed' ? 'bg-white/20' : 'bg-[#10B981] text-white'
                }`}>
                  {completedOrders.length}
                </span>
              )}
            </button>
          </div>

          {/* Active Orders */}
          {ordersView === 'active' && (
          <>
          {activeOrders.length > 0 ? (
            <div className="space-y-3">
              {activeOrders.map((order) => {
                const isExpanded = expandedOrderId === order.id;
                const orderTitle = order.isCartOrder 
                  ? (order.crafterItems?.length === 1 
                      ? order.crafterItems[0].title
                      : order.crafterItems?.map(item => item.title).join(', ') || 'Ordine')
                  : order.title;
                const orderTotal = order.isCartOrder
                  ? (order.crafterItems || []).reduce((sum, item) => sum + (item.totalPrice * item.quantity), 0)
                  : parseFloat(order.agreed_price || 0);
                
                return (
                  <Card 
                    key={order.id} 
                    className={`overflow-hidden transition-all ${isExpanded ? 'ring-2 ring-[#008080]/30' : ''}`}
                    data-testid={`crafter-order-${order.id}`}
                  >
                    {/* Collapsed Header - Always Visible */}
                    <button
                      onClick={() => setExpandedOrderId(isExpanded ? null : order.id)}
                      className="w-full p-4 flex items-center gap-4 hover:bg-slate-50 transition-colors text-left"
                    >
                      {order.isCartOrder ? (
                        <div className="w-12 h-12 rounded-xl bg-[#008080]/10 flex items-center justify-center flex-shrink-0">
                          <ShoppingCart className="w-6 h-6 text-[#008080]" />
                        </div>
                      ) : (
                        <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0">
                          <Hammer className="w-6 h-6 text-purple-600" />
                        </div>
                      )}
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="font-semibold truncate">{orderTitle}</span>
                          {order.isCartOrder ? (
                            <Badge className="bg-[#008080]/10 text-[#008080] border-[#008080]/20 text-xs">
                              Catalogo
                            </Badge>
                          ) : (
                            <Badge className="bg-purple-100 text-purple-700 border-purple-200 text-xs">
                              <Hammer className="w-3 h-3 mr-1" />
                              Su Richiesta
                            </Badge>
                          )}
                          <Badge className={`text-xs ${getOrderStatusColor(order.status)}`}>
                            {getOrderStatusLabel(order.status)}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-3 text-sm text-slate-500">
                          <span className="font-mono text-xs">{order.order_number || `ORD-${order.id?.slice(0, 8).toUpperCase()}`}</span>
                          <span className="text-slate-300">•</span>
                          <span className="font-semibold text-[#008080]">€{orderTotal.toFixed(2)}</span>
                          <span className="text-slate-300">•</span>
                          <span>{new Date(order.created_at).toLocaleDateString('it-IT')}</span>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5 text-slate-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-slate-400" />
                        )}
                      </div>
                    </button>
                    
                    {/* Expanded Details */}
                    {isExpanded && (
                      <div className="border-t bg-slate-50/50 p-5 space-y-4">
                        {/* Products List */}
                        {order.isCartOrder ? (
                          <div className="space-y-3 bg-white rounded-lg p-4 border">
                            <p className="text-sm font-medium text-slate-700 mb-2">Prodotti da spedire</p>
                            {(order.crafterItems || []).map((item, idx) => (
                              <div key={idx}>
                                <div className="flex items-center gap-3">
                                  <div className="w-14 h-14 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0">
                                    {item.image ? (
                                      <img src={item.image} alt={item.title} className="w-full h-full object-cover" />
                                    ) : (
                                      <div className="w-full h-full flex items-center justify-center text-slate-400">
                                        <Package className="w-6 h-6" />
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium truncate">{item.title}</p>
                                    {item.selectedVariants && Object.keys(item.selectedVariants).length > 0 && (
                                      <p className="text-xs text-slate-500">
                                        {Object.entries(item.selectedVariants).map(([k, v]) => `${k}: ${v}`).join(', ')}
                                      </p>
                                    )}
                                  </div>
                                  <div className="text-right flex-shrink-0">
                                    <p className="font-semibold">€{(item.totalPrice || 0).toFixed(2)}</p>
                                    <p className="text-xs text-slate-500">x{item.quantity}</p>
                                  </div>
                                </div>
                                {/* Personalizzazioni cliente (testi + file/immagini) */}
                                <OrderItemCustomizationBlock item={item} />
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="bg-white rounded-lg p-4 border">
                            <p className="text-sm font-medium text-slate-700 mb-2">Dettaglio richiesta</p>
                            <p className="text-sm text-slate-600">{order.description || 'Nessuna descrizione'}</p>
                            {/* Allegati caricati dal cliente nella richiesta */}
                            <OrderItemCustomizationBlock item={order.customizationItem || {}} />
                          </div>
                        )}

                        {/* Customer & Shipping Info */}
                        <div className="grid md:grid-cols-2 gap-4">
                          <div className="bg-white border rounded-lg p-3">
                            <p className="text-slate-500 flex items-center gap-1 mb-1 text-sm">
                              <User className="w-3 h-3" /> Cliente
                            </p>
                            <p className="font-medium">{order.client?.full_name || order.shipping_address?.fullName || 'N/A'}</p>
                          </div>
                          {order.shipping_address && (
                            <div className="bg-white border rounded-lg p-3">
                              <p className="text-slate-500 flex items-center gap-1 mb-1 text-sm">
                                <MapPin className="w-3 h-3" /> Spedire a
                              </p>
                              <p className="font-medium">{order.shipping_address.fullName}</p>
                              <p className="text-slate-600 text-xs">
                                {order.shipping_address.address}, {order.shipping_address.postalCode} {order.shipping_address.city} ({order.shipping_address.province})
                              </p>
                            </div>
                          )}
                        </div>
                        
                        {/* Progress Images Section - Only when in_progress */}
                        {order.status === 'in_progress' && (
                          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                            <div className="flex items-center gap-2 mb-3">
                              <ImageIcon className="w-5 h-5 text-amber-600" />
                              <h4 className="font-semibold text-amber-800">Aggiorna il cliente sui progressi</h4>
                            </div>
                            <p className="text-sm text-amber-700 mb-4">
                              Carica foto della lavorazione per tenere aggiornato il cliente. Riceverà una notifica automatica!
                            </p>
                            
                            {/* Previously uploaded progress images */}
                            {order.progress_images && order.progress_images.length > 0 && (
                              <div className="mb-4">
                                <p className="text-xs text-slate-500 mb-2">Immagini già inviate ({order.progress_images.length})</p>
                                <div className="flex flex-wrap gap-2">
                                  {order.progress_images.map((url, idx) => (
                                    <img 
                                      key={idx} 
                                      src={url} 
                                      alt={`Progresso ${idx + 1}`} 
                                      className="w-16 h-16 object-cover rounded border border-slate-200"
                                    />
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {/* Upload new images */}
                            <div className="space-y-3">
                              {progressImages.length < 5 && (
                                <label className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-amber-300 rounded-lg cursor-pointer hover:border-amber-500 hover:bg-amber-100/50 transition-colors">
                                  {uploadingProgress ? (
                                    <>
                                      <Loader2 className="w-5 h-5 animate-spin text-amber-600" />
                                      <span className="text-amber-700">Caricamento...</span>
                                    </>
                                  ) : (
                                    <>
                                      <Upload className="w-5 h-5 text-amber-600" />
                                      <span className="text-amber-700 font-medium">Carica immagini progresso</span>
                                    </>
                                  )}
                                  <input
                                    type="file"
                                    accept=".jpg,.jpeg,.png,.webp"
                                    multiple
                                    onChange={handleProgressImageUpload}
                                    disabled={uploadingProgress}
                                    className="hidden"
                                  />
                                </label>
                              )}
                              
                              {/* Preview uploaded images */}
                              {progressImages.length > 0 && (
                                <div className="flex flex-wrap gap-2">
                                  {progressImages.map((img, idx) => (
                                    <div key={idx} className="relative group">
                                      <img 
                                        src={img.url} 
                                        alt={`Nuova ${idx + 1}`} 
                                        className="w-20 h-20 object-cover rounded-lg border-2 border-amber-400"
                                      />
                                      <button
                                        onClick={() => handleRemoveProgressImage(idx)}
                                        className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                      >
                                        <X className="w-3 h-3" />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}
                              
                              {/* Message input */}
                              {progressImages.length > 0 && (
                                <>
                                  <Textarea
                                    placeholder="Aggiungi un messaggio per il cliente (opzionale)..."
                                    value={progressMessage}
                                    onChange={(e) => setProgressMessage(e.target.value)}
                                    className="resize-none"
                                    rows={2}
                                  />
                                  
                                  {/* Add to Portfolio toggle */}
                                  <div 
                                    className={`flex items-center justify-between p-3 rounded-lg border-2 transition-all cursor-pointer ${
                                      addToPortfolio 
                                        ? 'border-purple-400 bg-purple-50' 
                                        : 'border-slate-200 bg-white hover:border-purple-200'
                                    }`}
                                    onClick={() => setAddToPortfolio(!addToPortfolio)}
                                  >
                                    <div className="flex items-center gap-3">
                                      <div className={`p-2 rounded-lg ${addToPortfolio ? 'bg-purple-500' : 'bg-slate-100'}`}>
                                        <Images className={`w-4 h-4 ${addToPortfolio ? 'text-white' : 'text-slate-500'}`} />
                                      </div>
                                      <div>
                                        <p className={`font-medium text-sm ${addToPortfolio ? 'text-purple-700' : 'text-slate-700'}`}>
                                          Aggiungi al Portfolio
                                        </p>
                                        <p className="text-xs text-slate-500">
                                          Mostra queste foto nella tua vetrina
                                        </p>
                                      </div>
                                    </div>
                                    <Switch
                                      checked={addToPortfolio}
                                      onCheckedChange={setAddToPortfolio}
                                      className="data-[state=checked]:bg-purple-500"
                                    />
                                  </div>
                                  
                                  <Button
                                    onClick={() => handleSendProgressUpdate(order)}
                                    disabled={sendingProgress}
                                    className="w-full bg-amber-500 text-white hover:bg-amber-600"
                                  >
                                    {sendingProgress ? (
                                      <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Invio in corso...
                                      </>
                                    ) : (
                                      <>
                                        <Send className="w-4 h-4 mr-2" />
                                        Invia aggiornamento al cliente
                                      </>
                                    )}
                                  </Button>
                                </>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Order Summary & Actions */}
                        <div className="flex items-center justify-between pt-3 border-t border-slate-200">
                          <div className="text-sm text-slate-500">
                            <span>Metodo: {order.shipping_method === 'express' ? 'Express' : 'Standard'}</span>
                          </div>
                          <div className="flex gap-2">
                            {order.status === 'confirmed' && (
                              <Button 
                                size="sm" 
                                onClick={() => handleStartOrder(order.id, order.isCartOrder)}
                                className="bg-[#DAA520] text-white hover:bg-[#B8860B]"
                              >
                                <PlayCircle className="w-4 h-4 mr-1" />
                                Inizia Lavorazione
                              </Button>
                            )}
                            
                            {order.status === 'in_progress' && (
                              <Button 
                                size="sm" 
                                onClick={() => handleMarkReady(order.id, order.isCartOrder)}
                                className="bg-green-500 text-white hover:bg-green-600"
                              >
                                <CheckCircle2 className="w-4 h-4 mr-1" />
                                Segna Pronto
                              </Button>
                            )}
                            
                            {order.status === 'ready' && (
                              <Button 
                                size="sm" 
                                onClick={() => handleOpenShippingDialog(order)}
                                className="bg-indigo-500 text-white hover:bg-indigo-600"
                              >
                                <Truck className="w-4 h-4 mr-1" />
                                Spedisci
                              </Button>
                            )}
                            
                            {order.status === 'shipped' && order.tracking_number && (
                              <Badge variant="outline" className="py-1">
                                <Truck className="w-3 h-3 mr-1" />
                                {order.tracking_number}
                              </Badge>
                            )}

                            {['confirmed', 'in_progress', 'ready'].includes(order.status) && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openCancelDialog(order)}
                                className="border-red-300 text-red-600 hover:bg-red-50"
                                data-testid={`crafter-cancel-order-${order.id}`}
                              >
                                <XCircle className="w-4 h-4 mr-1" />
                                Annulla ordine
                              </Button>
                            )}
                          </div>
                        </div>
                        
                        {/* Progress bar */}
                        <div className="pt-3 border-t border-slate-200">
                          <div className="flex items-center justify-between text-xs">
                            {['confirmed', 'in_progress', 'ready', 'shipped'].map((step, idx) => {
                              const steps = ['confirmed', 'in_progress', 'ready', 'shipped'];
                              const currentIdx = steps.indexOf(order.status);
                              const isCompleted = idx <= currentIdx;
                              const isCurrent = idx === currentIdx;
                              
                              return (
                                <div key={step} className="flex-1 flex flex-col items-center">
                                  <div className={`w-3 h-3 rounded-full mb-1 ${
                                    isCompleted ? 'bg-[#DAA520]' : 'bg-slate-200'
                                  } ${isCurrent ? 'ring-2 ring-[#DAA520]/30' : ''}`} />
                                  <span className={`text-center ${isCompleted ? 'text-[#DAA520] font-medium' : 'text-slate-400'}`}>
                                    {getOrderStatusLabel(step)}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card className="p-12 text-center border-dashed border-2">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Package className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-700 mb-2">Nessun ordine da gestire</h3>
              <p className="text-slate-500">Quando un cliente acquista i tuoi prodotti, l'ordine apparirà qui</p>
            </Card>
          )}
          </>
          )}

          {/* Completed Orders Section */}
          {ordersView === 'completed' && (
            <>
            {completedOrders.length > 0 ? (
              <div className="space-y-3">
                {completedOrders.map((order, index) => (
                  <Card key={order.id} className={`p-4 ${order.isCartOrder ? 'bg-gradient-to-r from-teal-50/50 to-transparent' : 'bg-gradient-to-r from-purple-50/50 to-transparent'}`}>
                    <div className="flex items-center gap-4">
                      {order.isCartOrder ? (
                        <div className="w-12 h-12 rounded-xl bg-[#008080]/10 flex items-center justify-center flex-shrink-0">
                          <ShoppingCart className="w-6 h-6 text-[#008080]" />
                        </div>
                      ) : (
                        <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0">
                          <Hammer className="w-6 h-6 text-purple-600" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold truncate">
                            {order.isCartOrder 
                              ? (order.crafterItems?.length === 1 
                                  ? order.crafterItems[0].title
                                  : order.crafterItems?.map(item => item.title).join(', ') || 'Ordine')
                              : order.title
                            }
                          </span>
                          {order.isCartOrder ? (
                            <Badge className="bg-[#008080]/10 text-[#008080] border-[#008080]/20">
                              <ShoppingCart className="w-3 h-3 mr-1" />
                              Catalogo
                            </Badge>
                          ) : (
                            <Badge className="bg-purple-100 text-purple-700 border-purple-200">
                              <Hammer className="w-3 h-3 mr-1" />
                              Su Richiesta
                            </Badge>
                          )}
                          {order.status === 'cancelled' ? (
                            <Badge className="bg-red-100 text-red-700 border-red-200" data-testid="crafter-order-cancelled-badge">
                              <XCircle className="w-3 h-3 mr-1" />
                              Annullato
                            </Badge>
                          ) : (
                            <Badge className="bg-green-100 text-green-700 border-green-200">
                              <CheckCircle2 className="w-3 h-3 mr-1" />
                              Completato
                            </Badge>
                          )}
                        </div>
                        {order.order_number && (
                          <span className="text-xs font-mono text-slate-400">{order.order_number}</span>
                        )}
                        {order.status === 'cancelled' && order.cancellation_reason && (
                          <p className="text-xs text-red-600 mt-1" data-testid="crafter-order-cancellation-reason">
                            Motivo: {order.cancellation_reason}
                            {order.cancelled_by && (
                              <span className="text-slate-400"> · annullato {order.cancelled_by === 'crafter' ? 'da te' : 'dal cliente'}</span>
                            )}
                          </p>
                        )}
                        <div className="flex items-center gap-3 text-sm text-slate-500 mt-1 flex-wrap">
                          <span className={`font-semibold ${order.status === 'cancelled' ? 'text-red-700 line-through' : 'text-green-700'}`}>
                            €{order.isCartOrder 
                              ? (order.crafterItems || []).reduce((sum, item) => sum + (item.totalPrice * item.quantity), 0).toFixed(2)
                              : order.agreed_price
                            }
                          </span>
                          <span className="text-slate-400">•</span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(order.cancelled_at || order.completed_at || order.created_at).toLocaleDateString('it-IT', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric'
                            })}
                          </span>
                          {order.client && (
                            <>
                              <span className="text-slate-400">•</span>
                              <span className="flex items-center gap-1">
                                <User className="w-3 h-3" />
                                {order.client.full_name}
                              </span>
                            </>
                          )}
                          {/* Show product names for cart orders */}
                          {order.isCartOrder && order.crafterItems?.length > 0 && (
                            <>
                              <span className="text-slate-400">•</span>
                              <span className="text-xs text-slate-400 truncate max-w-[200px]">
                                {order.crafterItems.map(i => i.title).join(', ')}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="p-12 text-center border-dashed border-2">
                <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <History className="w-8 h-8 text-green-400" />
                </div>
                <h3 className="text-lg font-semibold text-slate-700 mb-2">Nessun ordine completato</h3>
                <p className="text-slate-500">Gli ordini completati appariranno qui</p>
              </Card>
            )}
            </>
          )}

          {/* No Images Warning Dialog */}
          <Dialog open={showNoImagesWarning} onOpenChange={setShowNoImagesWarning}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                  Nessuna immagine di progresso
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <p className="text-slate-600">
                  Non hai ancora condiviso immagini del progresso con il cliente. 
                  Ti consigliamo di caricare almeno una foto del prodotto finito prima di segnarlo come pronto.
                </p>
                <p className="text-sm text-slate-500">
                  Condividere foto aumenta la fiducia del cliente e riduce richieste di chiarimenti!
                </p>
                <div className="flex gap-3 pt-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      handleMarkReady(pendingReadyOrderId, true);
                    }}
                    className="flex-1"
                  >
                    Procedi comunque
                  </Button>
                  <Button
                    onClick={() => {
                      setShowNoImagesWarning(false);
                      setPendingReadyOrderId(null);
                    }}
                    className="flex-1 bg-amber-500 hover:bg-amber-600"
                  >
                    Carica immagini
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Shipping Dialog — nuovo Shipment Engine multi-carrier */}
          <ShipmentConfirmDialog
            open={shippingDialogOpen}
            onOpenChange={setShippingDialogOpen}
            order={selectedOrderForShipping}
            sellerId={user?.id}
            onSuccess={handleShipmentCreated}
          />

          {/* Dialog annullamento ordine (artigiano) — scoraggiato */}
          <Dialog open={!!cancelOrderTarget} onOpenChange={(o) => { if (!o) setCancelOrderTarget(null); }}>
            <DialogContent className="max-w-md" data-testid="crafter-cancel-order-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-amber-700">
                  <AlertTriangle className="w-5 h-5" />
                  Prima di annullare, proviamo a trovare una soluzione?
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900 space-y-2">
                  <p>
                    Capita di incontrare un imprevisto, e va bene così. Prima di annullare, ti consigliamo di
                    <strong> contattare l'acquirente tramite chat</strong>: spesso un piccolo ritardo o una modifica
                    concordata bastano a salvare l'ordine e a mantenere felice il cliente.
                  </p>
                  <p>
                    L'annullamento resta l'ultima scelta, da usare solo quando il problema non è davvero risolvibile.
                    Tieni presente che annullare un ordine già accettato <strong>incide sulla tua affidabilità</strong> e
                    sulla tua visibilità nei risultati. Il cliente riceverà un rimborso totale immediato
                    (accredito entro 5–10 giorni lavorativi).
                  </p>
                </div>
                {cancelReliability && (
                  <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm text-slate-600" data-testid="crafter-reliability-info">
                    Affidabilità attuale: <strong className="capitalize">{cancelReliability.reliability_label}</strong>
                    {' '}· Annullamenti: <strong>{cancelReliability.artisan_cancellations}</strong>
                    {cancelReliability.total_orders > 0 && (
                      <> su {cancelReliability.total_orders} ordini</>
                    )}
                  </div>
                )}
                <div>
                  <label className="text-sm font-medium text-slate-700">Motivo dell'annullamento *</label>
                  <Textarea
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    placeholder="Spiega al cliente perché devi annullare (obbligatorio)…"
                    className="mt-1"
                    rows={3}
                    data-testid="crafter-cancel-reason-input"
                  />
                </div>
                <div className="flex flex-wrap gap-2 justify-end">
                  <Button variant="outline" onClick={() => setCancelOrderTarget(null)} disabled={cancelling}>
                    Torna indietro
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleContactBuyerFromCancel}
                    disabled={cancelling}
                    className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                    data-testid="crafter-cancel-contact-buyer-btn"
                  >
                    <MessageSquare className="w-4 h-4 mr-1" />
                    Contatta l'acquirente
                  </Button>
                  <Button
                    onClick={handleCancelOrderCrafter}
                    disabled={cancelling || !cancelReason.trim()}
                    className="bg-red-600 hover:bg-red-700 text-white"
                    data-testid="crafter-cancel-confirm-btn"
                  >
                    {cancelling ? 'Annullamento…' : 'Conferma annullamento'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {/* ===================== SEZIONE PAGAMENTI - REGISTRAZIONE STRIPE ===================== */}
      {mainTab === 'stripe-setup' && (
        <div className="mt-4 space-y-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 rounded-xl bg-gradient-to-br from-[#008080] to-teal-600 text-white">
              <CreditCard className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Registrazione Pagamenti</h2>
              <p className="text-sm text-slate-500">Configura il tuo account per ricevere i pagamenti</p>
            </div>
          </div>

          {/* Status Card */}
          <Card className={`overflow-hidden border-2 ${stripeConnected ? 'border-green-200' : 'border-amber-200'}`}>
            <div className={`p-4 ${stripeConnected ? 'bg-gradient-to-r from-green-50 to-emerald-50' : 'bg-gradient-to-r from-amber-50 to-orange-50'}`}>
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-full ${stripeConnected ? 'bg-green-100' : 'bg-amber-100'}`}>
                  {stripeConnected ? (
                    <CheckCircle2 className="w-8 h-8 text-green-600" />
                  ) : (
                    <AlertTriangle className="w-8 h-8 text-amber-600" />
                  )}
                </div>
                <div className="flex-1">
                  <h3 className={`font-bold text-lg ${stripeConnected ? 'text-green-800' : 'text-amber-800'}`}>
                    {stripeConnected ? 'Account Stripe Attivo' : 'Registrazione Incompleta'}
                  </h3>
                  <p className={`text-sm ${stripeConnected ? 'text-green-700' : 'text-amber-700'}`}>
                    {stripeConnected 
                      ? 'Il tuo account è configurato correttamente per ricevere pagamenti'
                      : 'Completa la registrazione per poter prelevare i tuoi guadagni'
                    }
                  </p>
                </div>
                {stripeConnected && (
                  <Badge className="bg-green-100 text-green-700 border-green-200">
                    <Check className="w-3 h-3 mr-1" />
                    Verificato
                  </Badge>
                )}
              </div>
            </div>

            <div className="p-6">
              {/* Pending Balance Warning */}
              {!stripeConnected && pendingBalance.ordersCount > 0 && (
                <div className="mb-6 p-4 rounded-lg bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-200">
                  <div className="flex items-center gap-3">
                    <Wallet className="w-10 h-10 text-amber-600" />
                    <div>
                      <p className="font-bold text-amber-800 text-lg">
                        €{pendingBalance.amount.toFixed(2)} in attesa di prelievo
                      </p>
                      <p className="text-sm text-amber-700">
                        Da {pendingBalance.ordersCount} ordin{pendingBalance.ordersCount === 1 ? 'e' : 'i'} completat{pendingBalance.ordersCount === 1 ? 'o' : 'i'}. 
                        Completa la registrazione per incassare!
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Registration Steps */}
              <div className="space-y-4">
                <h4 className="font-semibold text-slate-700 mb-3">Stato della registrazione</h4>
                
                {/* Step 1: Account Created */}
                <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50">
                  <div className={`p-2 rounded-full ${stripeAccountDetails?.has_account ? 'bg-green-100' : 'bg-slate-200'}`}>
                    {stripeAccountDetails?.has_account ? (
                      <Check className="w-4 h-4 text-green-600" />
                    ) : (
                      <Building className="w-4 h-4 text-slate-400" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className={`font-medium ${stripeAccountDetails?.has_account ? 'text-green-700' : 'text-slate-600'}`}>
                      Account Stripe creato
                    </p>
                    <p className="text-xs text-slate-500">
                      {stripeAccountDetails?.has_account ? 'Completato' : 'Da completare'}
                    </p>
                  </div>
                </div>

                {/* Step 2: Details Submitted */}
                <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50">
                  <div className={`p-2 rounded-full ${stripeAccountDetails?.details_submitted ? 'bg-green-100' : 'bg-slate-200'}`}>
                    {stripeAccountDetails?.details_submitted ? (
                      <Check className="w-4 h-4 text-green-600" />
                    ) : (
                      <FileText className="w-4 h-4 text-slate-400" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className={`font-medium ${stripeAccountDetails?.details_submitted ? 'text-green-700' : 'text-slate-600'}`}>
                      Dati personali e aziendali
                    </p>
                    <p className="text-xs text-slate-500">
                      {stripeAccountDetails?.details_submitted ? 'Completato' : 'Inserisci i tuoi dati'}
                    </p>
                  </div>
                </div>

                {/* Step 3: Identity Verification (KYC) */}
                <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50">
                  <div className={`p-2 rounded-full ${stripeConnected ? 'bg-green-100' : 'bg-slate-200'}`}>
                    {stripeConnected ? (
                      <Check className="w-4 h-4 text-green-600" />
                    ) : (
                      <Shield className="w-4 h-4 text-slate-400" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className={`font-medium ${stripeConnected ? 'text-green-700' : 'text-slate-600'}`}>
                      Verifica identità (KYC)
                    </p>
                    <p className="text-xs text-slate-500">
                      {stripeConnected ? 'Verificato' : 'Richiesta verifica documenti'}
                    </p>
                  </div>
                </div>

                {/* Step 4: Payouts Enabled */}
                <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50">
                  <div className={`p-2 rounded-full ${stripeAccountDetails?.payouts_enabled ? 'bg-green-100' : 'bg-slate-200'}`}>
                    {stripeAccountDetails?.payouts_enabled ? (
                      <Check className="w-4 h-4 text-green-600" />
                    ) : (
                      <Banknote className="w-4 h-4 text-slate-400" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className={`font-medium ${stripeAccountDetails?.payouts_enabled ? 'text-green-700' : 'text-slate-600'}`}>
                      Prelievi abilitati
                    </p>
                    <p className="text-xs text-slate-500">
                      {stripeAccountDetails?.payouts_enabled ? 'Puoi prelevare i fondi' : 'Disponibile dopo la verifica'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Action Button */}
              <div className="mt-6 pt-4 border-t">
                {!stripeConnected ? (
                  <StripeConnectCard 
                    crafterId={user?.id} 
                    crafterEmail={profile?.email} 
                    variant="full"
                    hideWhenConnected={false}
                    onStatusChange={checkStripeStatus}
                  />
                ) : (
                  <div className="flex items-center justify-between p-4 rounded-lg bg-green-50 border border-green-200">
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="w-6 h-6 text-green-600" />
                      <div>
                        <p className="font-medium text-green-800">Tutto pronto!</p>
                        <p className="text-sm text-green-700">Puoi ricevere pagamenti e prelevare i guadagni</p>
                      </div>
                    </div>
                    <Button 
                      variant="outline"
                      className="border-green-300 text-green-700 hover:bg-green-100"
                      onClick={() => navigate('/dashboard?tab=artigiano&section=payouts')}
                    >
                      Vai ai Prelievi
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </Card>

          {/* Info Card */}
          <Card className="p-4 bg-slate-50 border-slate-200">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-slate-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-slate-600">
                <p className="font-medium text-slate-700 mb-1">Perché devo registrarmi?</p>
                <p>
                  Stripe è il nostro partner per i pagamenti sicuri. La registrazione è richiesta dalla normativa europea 
                  per verificare la tua identità e permetterti di ricevere i pagamenti delle vendite sul tuo conto bancario.
                </p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* ===================== SEZIONE PAGAMENTI - PRELIEVO INCASSI ===================== */}
      {mainTab === 'payouts' && (
        <div className="mt-4 space-y-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 rounded-xl bg-gradient-to-br from-[#DAA520] to-amber-600 text-white">
              <Wallet className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Prelievo Incassi</h2>
              <p className="text-sm text-slate-500">Gestisci i tuoi guadagni e preleva i fondi</p>
            </div>
          </div>

          {!stripeConnected ? (
            /* Not connected - redirect to setup */
            <Card className="p-6 border-2 border-amber-200 bg-amber-50">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-100 flex items-center justify-center">
                  <Lock className="w-8 h-8 text-amber-600" />
                </div>
                <h3 className="font-bold text-lg text-amber-800 mb-2">
                  Registrazione Stripe richiesta
                </h3>
                <p className="text-amber-700 mb-4">
                  Per accedere ai tuoi incassi e richiedere prelievi, devi prima completare la registrazione Stripe.
                </p>
                {pendingBalance.ordersCount > 0 && (
                  <div className="mb-4 p-3 rounded-lg bg-white/50 border border-amber-300">
                    <p className="font-semibold text-amber-800">
                      Hai €{pendingBalance.amount.toFixed(2)} da {pendingBalance.ordersCount} ordin{pendingBalance.ordersCount === 1 ? 'e' : 'i'} in attesa!
                    </p>
                  </div>
                )}
                <Button 
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  onClick={() => navigate('/dashboard?tab=artigiano&section=stripe-setup')}
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  Completa la Registrazione
                </Button>
              </div>
            </Card>
          ) : (
            /* Connected - show payout options */
            <>
              {/* Balance Overview */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="p-5 bg-gradient-to-br from-[#008080] to-teal-600 text-white">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-white/80 text-sm">Saldo disponibile</span>
                    <Wallet className="w-5 h-5 text-white/60" />
                  </div>
                  <p className="text-3xl font-bold">€{pendingBalance.amount.toFixed(2)}</p>
                  <p className="text-white/70 text-sm mt-1">
                    Da {pendingBalance.ordersCount} ordin{pendingBalance.ordersCount === 1 ? 'e' : 'i'} completat{pendingBalance.ordersCount === 1 ? 'o' : 'i'}
                  </p>
                </Card>

                <Card className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-slate-500 text-sm">Stato account</span>
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                  </div>
                  <p className="text-xl font-bold text-green-600">Attivo</p>
                  <p className="text-slate-500 text-sm mt-1">Prelievi abilitati</p>
                </Card>
              </div>

              {/* Stripe Dashboard Link */}
              <Card className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-xl bg-[#635BFF]/10">
                      <svg className="w-8 h-8" viewBox="0 0 32 32" fill="none">
                        <path d="M13.976 13.176c0-.942.77-1.304 2.044-1.304 1.826 0 4.132.554 5.958 1.542V8.09c-1.998-.793-3.967-1.107-5.958-1.107-4.87 0-8.104 2.54-8.104 6.785 0 6.62 9.112 5.562 9.112 8.42 0 1.115-.97 1.477-2.33 1.477-2.015 0-4.59-.83-6.63-1.942v5.38c2.256.97 4.532 1.38 6.63 1.38 4.99 0 8.424-2.47 8.424-6.77-.018-7.145-9.146-5.877-9.146-8.537z" fill="#635BFF"/>
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-bold text-lg">Dashboard Stripe</h3>
                      <p className="text-sm text-slate-500">
                        Gestisci pagamenti, visualizza transazioni e richiedi prelievi
                      </p>
                    </div>
                  </div>
                  <Button 
                    className="bg-[#635BFF] hover:bg-[#5851DB] text-white"
                    onClick={async () => {
                      try {
                        const response = await fetch(`${API_URL}/api/connect/dashboard-link/${user.id}`);
                        if (response.ok) {
                          const data = await response.json();
                          if (data.url) {
                            window.open(data.url, '_blank');
                          }
                        } else {
                          toast.error('Impossibile accedere alla dashboard Stripe');
                        }
                      } catch (error) {
                        toast.error('Errore di connessione');
                      }
                    }}
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Apri Dashboard
                  </Button>
                </div>
              </Card>

              {/* Quick Actions */}
              <Card className="p-6">
                <h4 className="font-semibold text-slate-700 mb-4">Azioni rapide</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <Button 
                    variant="outline" 
                    className="h-auto py-4 flex flex-col items-center gap-2"
                    onClick={async () => {
                      try {
                        const response = await fetch(`${API_URL}/api/connect/dashboard-link/${user.id}`);
                        if (response.ok) {
                          const data = await response.json();
                          if (data.url) {
                            window.open(data.url + '#/payouts', '_blank');
                          }
                        }
                      } catch (error) {
                        toast.error('Errore di connessione');
                      }
                    }}
                  >
                    <Banknote className="w-6 h-6 text-[#008080]" />
                    <span className="font-medium">Richiedi Prelievo</span>
                    <span className="text-xs text-slate-500">Trasferisci fondi sul conto</span>
                  </Button>

                  <Button 
                    variant="outline" 
                    className="h-auto py-4 flex flex-col items-center gap-2"
                    onClick={async () => {
                      try {
                        const response = await fetch(`${API_URL}/api/connect/dashboard-link/${user.id}`);
                        if (response.ok) {
                          const data = await response.json();
                          if (data.url) {
                            window.open(data.url + '#/payments', '_blank');
                          }
                        }
                      } catch (error) {
                        toast.error('Errore di connessione');
                      }
                    }}
                  >
                    <History className="w-6 h-6 text-[#DAA520]" />
                    <span className="font-medium">Storico Transazioni</span>
                    <span className="text-xs text-slate-500">Visualizza tutti i pagamenti</span>
                  </Button>

                  <Button 
                    variant="outline" 
                    className="h-auto py-4 flex flex-col items-center gap-2"
                    onClick={() => {
                      checkStripeStatus();
                      toast.success('Stato aggiornato');
                    }}
                  >
                    <RefreshCw className="w-6 h-6 text-slate-600" />
                    <span className="font-medium">Aggiorna Stato</span>
                    <span className="text-xs text-slate-500">Sincronizza con Stripe</span>
                  </Button>
                </div>
              </Card>

              {/* Info */}
              <Card className="p-4 bg-blue-50 border-blue-200">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-700">
                    <p className="font-medium text-blue-800 mb-1">Come funzionano i prelievi?</p>
                    <p>
                      I fondi delle vendite vengono accumulati nel tuo account Stripe. Puoi richiedere un prelievo 
                      in qualsiasi momento dalla dashboard Stripe. I tempi di accredito dipendono dalla tua banca 
                      (generalmente 2-3 giorni lavorativi).
                    </p>
                  </div>
                </div>
              </Card>
            </>
          )}
        </div>
      )}

      {/* CREAZIONI */}
      {mainTab === 'products' && (
        <div className="mt-4">
          {/* Product list section */}

          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold">Le Mie Creazioni</h3>
            <Dialog open={isProductDialogOpen} onOpenChange={setIsProductDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#DAA520] text-white" onClick={handleOpenNewProduct}>
                  <Plus className="w-4 h-4 mr-2" />Aggiungi
                </Button>
              </DialogTrigger>
              <DialogContent 
                className="max-w-4xl max-h-[90vh]"
                hideCloseButton={true}
                onInteractOutside={(e) => e.preventDefault()}
                onEscapeKeyDown={(e) => e.preventDefault()}
                onPointerDownOutside={(e) => e.preventDefault()}
              >
                <DialogHeader>
                  <DialogTitle>{editingProduct ? 'Modifica Creazione' : 'Aggiungi Creazione'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddProduct} className="mt-4">
                  <ProductForm
                    productTitle={productTitle} setProductTitle={setProductTitle}
                    productDescription={productDescription} setProductDescription={setProductDescription}
                    baseImages={baseImages} setBaseImages={setBaseImages}
                    mainImageIndex={mainImageIndex} setMainImageIndex={setMainImageIndex}
                    basePrice={basePrice} setBasePrice={setBasePrice}
                    crafterDiscount={crafterDiscount} setCrafterDiscount={setCrafterDiscount}
                    baseTimeline={baseTimeline} setBaseTimeline={setBaseTimeline}
                    commissionTermsAccepted={commissionTermsAccepted} setCommissionTermsAccepted={setCommissionTermsAccepted}
                    selectedCategory={selectedCategory} setSelectedCategory={setSelectedCategory}
                    categories={formCategories}
                    categoriesLoading={categoriesLoading}
                    hasVariants={hasVariants} setHasVariants={setHasVariants}
                    variants={variants} setVariants={setVariants}
                    hasCustomizations={hasCustomizations} setHasCustomizations={setHasCustomizations}
                    customizations={customizations} setCustomizations={setCustomizations}
                    inventory={inventory} setInventory={setInventory}
                    currentStep={productFormStep} setCurrentStep={setProductFormStep}
                    shippingRules={shippingRules}
                    selectedShippingRuleId={selectedShippingRuleId}
                    setSelectedShippingRuleId={setSelectedShippingRuleId}
                    highlights={productHighlights}
                    setHighlights={setProductHighlights}
                    onConfigureShipping={() => {
                      setIsProductDialogOpen(false);
                      navigate('/dashboard?tab=artigiano&section=shipping');
                    }}
                  />
                  
                  <div className="flex justify-end gap-2 pt-4 mt-4 border-t">
                    <Button type="button" variant="outline" onClick={() => { setIsProductDialogOpen(false); resetProductForm(); }}>Annulla</Button>
                    <Button type="submit" className="bg-[#DAA520] text-white">
                      {baseTimeline === 'immediate' && productFormStep === 1 
                        ? 'Avanti: Magazzino' 
                        : (editingProduct ? 'Salva Modifiche' : 'Crea Prodotto')
                      }
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {products.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {products.map((product) => {
                const mainImgIndex = product.customization_options?.main_image_index || 0;
                const mainImage = product.images?.[mainImgIndex] || product.images?.[0];
                const customCount = product.customization_options?.customizations?.length || 0;
                return (
                  <Card key={product.id} className="overflow-hidden group" data-testid={`product-card-${product.id}`}>
                    <div className="h-40 bg-slate-200 relative">
                      {mainImage && <img src={mainImage} alt={product.title} className="w-full h-full object-cover" />}
                      {customCount > 0 && (
                        <Badge className="absolute top-2 right-2 bg-[#DAA520] text-white text-xs">
                          {customCount} personalizzazioni
                        </Badge>
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold truncate">{product.title}</h3>
                      <p className="text-lg font-bold text-[#008080]">€{product.base_price}</p>
                      <div className="flex gap-2 mt-3">
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={() => handleEditProduct(product)} 
                          className="flex-1"
                          data-testid={`edit-product-${product.id}`}
                        >
                          <Edit className="w-3 h-3 mr-1" />Modifica
                        </Button>
                        <Button 
                          size="sm" 
                          variant="destructive" 
                          onClick={() => handleDeleteProduct(product.id)}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card className="p-8 text-center">
              <Package className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-600">Nessuna creazione</p>
            </Card>
          )}
        </div>
      )}

      {/* PORTFOLIO */}
      {mainTab === 'portfolio' && (
        <div className="mt-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold">Il Mio Portfolio</h3>
            <Dialog open={isPortfolioDialogOpen} onOpenChange={setIsPortfolioDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#DAA520] text-white"><ImageIcon className="w-4 h-4 mr-2" />Aggiungi</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Aggiungi al Portfolio</DialogTitle></DialogHeader>
                <form onSubmit={handleAddPortfolio} className="space-y-4 mt-4">
                  <div><Label>Titolo *</Label><Input value={portfolioTitle} onChange={(e) => setPortfolioTitle(e.target.value)} required /></div>
                  <div><Label>Descrizione</Label><Textarea value={portfolioDescription} onChange={(e) => setPortfolioDescription(e.target.value)} rows={3} /></div>
                  <ImageUploader label="Immagine *" currentImage={portfolioImage} onImageUploaded={(url) => setPortfolioImage(url)} bucketName="portfolio" />
                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setIsPortfolioDialogOpen(false)}>Annulla</Button>
                    <Button type="submit" className="bg-[#DAA520] text-white">Aggiungi</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {portfolio.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {portfolio.map((item) => (
                <Card key={item.id} className="overflow-hidden">
                  <div className="h-48 bg-slate-200">
                    <img src={item.image_url} alt={item.title} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-3">
                    <h3 className="font-semibold text-sm">{item.title}</h3>
                    <p className="text-xs text-slate-600 line-clamp-2">{item.description}</p>
                    <Button size="sm" variant="outline" onClick={() => handleDeletePortfolio(item.id)} className="w-full mt-2">
                      <Trash2 className="w-3 h-3 mr-1" />Rimuovi
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="p-8 text-center">
              <ImageIcon className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-600">Nessun elemento nel portfolio</p>
            </Card>
          )}
        </div>
      )}

      {/* MAGAZZINO */}
      {mainTab === 'inventory' && (
        <div className="mt-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold">Gestione Magazzino</h3>
          </div>
          
          {products.length === 0 ? (
            <Card className="p-8 text-center">
              <Warehouse className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-600 mb-2">Nessun prodotto da gestire</p>
              <p className="text-sm text-slate-500">Crea prima dei prodotti nella sezione Creazioni</p>
            </Card>
          ) : (
            <div className="space-y-4">
              {products.map((product) => {
                const hasVariants = product.customization_options?.hasVariants && product.customization_options?.variants?.length > 0;
                const variants = product.customization_options?.variants || [];
                const variantSettings = product.customization_options?.variantSettings || {};
                const defaultTimeline = product.customization_options?.timeline || 'immediate';
                const defaultInventory = product.customization_options?.inventory || {};
                
                // Generate all variant combinations or just default
                const getInventoryRows = () => {
                  if (!hasVariants) {
                    const settings = variantSettings['default'] || { timeline: defaultTimeline, stock: defaultInventory['default'] || 0 };
                    return [{ 
                      key: 'default', 
                      label: 'Prodotto base', 
                      timeline: settings.timeline || defaultTimeline,
                      stock: settings.stock ?? defaultInventory['default'] ?? 0 
                    }];
                  }
                  
                  // Generate combinations
                  const combinations = variants.reduce((acc, variant) => {
                    if (acc.length === 0) {
                      return variant.values.map(v => [{ type: variant.type, value: v }]);
                    }
                    const newCombinations = [];
                    acc.forEach(combo => {
                      variant.values.forEach(v => {
                        newCombinations.push([...combo, { type: variant.type, value: v }]);
                      });
                    });
                    return newCombinations;
                  }, []);
                  
                  return combinations.map(combo => {
                    const key = combo.map(c => `${c.type}:${c.value}`).join('|');
                    const label = combo.map(c => `${c.type}: ${c.value}`).join(' • ');
                    const settings = variantSettings[key] || { timeline: defaultTimeline, stock: defaultInventory[key] || 0 };
                    return { 
                      key, 
                      label, 
                      timeline: settings.timeline || defaultTimeline,
                      stock: settings.stock ?? defaultInventory[key] ?? 0 
                    };
                  });
                };
                
                const rows = getInventoryRows();
                
                return (
                  <Card key={product.id} className="p-4">
                    <div className="flex items-start gap-4">
                      {/* Product Image */}
                      <div className="w-20 h-20 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0">
                        {product.images?.[0] ? (
                          <img src={product.images[0]} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Package className="w-8 h-8 text-slate-300" />
                          </div>
                        )}
                      </div>
                      
                      {/* Product Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold text-lg truncate">{product.title}</h4>
                          <span className="text-sm text-slate-500">€{product.base_price}</span>
                        </div>
                        
                        {/* Inventory Table */}
                        <div className="bg-slate-50 rounded-lg p-3">
                          <div className="space-y-3">
                            {rows.map((row) => {
                              const isImmediate = row.timeline === 'immediate';
                              const timelineLabel = TIMELINE_OPTIONS.find(t => t.value === row.timeline)?.label || row.timeline;
                              
                              return (
                                <div key={row.key} className="p-3 bg-white rounded-lg border border-slate-200">
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="font-medium text-slate-700">{row.label}</span>
                                    <Badge className={isImmediate ? (row.stock > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700') : 'bg-amber-100 text-amber-700'}>
                                      {isImmediate ? (row.stock > 0 ? `${row.stock} disponibili` : 'Esaurito') : timelineLabel}
                                    </Badge>
                                  </div>
                                  
                                  <div className="flex items-center gap-3">
                                    {/* Availability Dropdown */}
                                    <div className="flex-1">
                                      <Label className="text-xs text-slate-500 mb-1 block">Disponibilità</Label>
                                      <Select 
                                        value={row.timeline} 
                                        onValueChange={(value) => handleChangeAvailability(product.id, row.key, value)}
                                      >
                                        <SelectTrigger className="h-9 text-sm">
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {TIMELINE_OPTIONS.map(option => (
                                            <SelectItem key={option.value} value={option.value}>
                                              {option.label}
                                            </SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    </div>
                                    
                                    {/* Stock Input (only for immediate) */}
                                    {isImmediate && (
                                      <div className="w-28">
                                        <Label className="text-xs text-slate-500 mb-1 block">Quantità</Label>
                                        <Input
                                          type="number"
                                          min="0"
                                          value={row.stock}
                                          onChange={(e) => handleUpdateStock(product.id, row.key, parseInt(e.target.value) || 0)}
                                          className="h-9 text-sm text-center"
                                        />
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* SPEDIZIONI - Shipping Rules Management */}
      {mainTab === 'shipping' && (
        <div className="mt-4">
          <ShippingRulesManager crafterId={user?.id} />
        </div>
      )}

      {/* INTERESSATI - Users who saved crafter's products */}
      {mainTab === 'fans' && (
        <div className="mt-4">
          <div className="mb-6">
            <h3 className="text-lg font-bold mb-2 flex items-center gap-2">
              <Heart className="w-5 h-5 text-rose-500" />
              Utenti Interessati ai Tuoi Prodotti
            </h3>
            <p className="text-sm text-slate-600">
              Questi utenti hanno salvato uno o più dei tuoi prodotti nei loro preferiti. Contattali per proporre offerte personalizzate!
            </p>
          </div>

          {productFans.length > 0 ? (
            <div className="space-y-4">
              {productFans.map((fan) => (
                <Card key={fan.user.id} className="p-5" data-testid={`fan-card-${fan.user.id}`}>
                  <div className="flex flex-col md:flex-row gap-4">
                    {/* User Info */}
                    <div className="flex items-start gap-3 flex-shrink-0">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-rose-400 to-pink-500 flex items-center justify-center text-white font-bold text-lg">
                        {fan.user.avatar_url ? (
                          <img src={fan.user.avatar_url} alt={fan.user.full_name} className="w-full h-full rounded-full object-cover" />
                        ) : (
                          (fan.user.full_name || 'U')[0].toUpperCase()
                        )}
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-800">{fan.user.full_name || 'Utente'}</h4>
                        <p className="text-xs text-slate-500">
                          Ha salvato {fan.savedProducts.length} prodott{fan.savedProducts.length === 1 ? 'o' : 'i'}
                        </p>
                        <p className="text-xs text-slate-400">
                          Ultimo salvataggio: {new Date(fan.latestSaveDate).toLocaleDateString('it-IT')}
                        </p>
                      </div>
                    </div>

                    {/* Saved Products */}
                    <div className="flex-1">
                      <p className="text-xs font-medium text-slate-500 mb-2">Prodotti salvati:</p>
                      <div className="flex flex-wrap gap-2">
                        {fan.savedProducts.map((product) => (
                          <div 
                            key={product.id} 
                            className="flex items-center gap-2 bg-slate-50 rounded-lg p-2 pr-3"
                          >
                            <div className="w-10 h-10 rounded-md overflow-hidden bg-white flex-shrink-0">
                              {product.images?.[0] ? (
                                <img src={product.images[0]} alt={product.title} className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-slate-300">
                                  <Package className="w-5 h-5" />
                                </div>
                              )}
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-slate-700 truncate max-w-[150px]">{product.title}</p>
                              <p className="text-xs text-[#008080] font-semibold">€{product.base_price?.toFixed(2)}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col gap-2 flex-shrink-0">
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button
                            size="sm"
                            className="bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white"
                            onClick={() => setOfferDiscountDialog({
                              userId: fan.user.id,
                              userName: fan.user.full_name,
                              productId: fan.savedProducts[0].id,
                              productTitle: fan.savedProducts[0].title
                            })}
                            data-testid={`offer-discount-${fan.user.id}`}
                          >
                            <Gift className="w-4 h-4 mr-2" />
                            Proponi Sconto
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="sm:max-w-md">
                          <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                              <Gift className="w-5 h-5 text-rose-500" />
                              Proponi Sconto Esclusivo
                            </DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4 py-4">
                            <div className="bg-rose-50 border border-rose-200 rounded-lg p-3">
                              <p className="text-sm text-rose-800">
                                Stai per inviare un'offerta speciale a <strong>{fan.user.full_name}</strong> per il prodotto che ha salvato.
                              </p>
                            </div>

                            {/* Product Selection (if multiple) */}
                            {fan.savedProducts.length > 1 && (
                              <div>
                                <Label className="mb-2 block">Seleziona il prodotto</Label>
                                <Select 
                                  value={offerDiscountDialog?.productId || fan.savedProducts[0].id}
                                  onValueChange={(value) => {
                                    const selectedProduct = fan.savedProducts.find(p => p.id === value);
                                    setOfferDiscountDialog(prev => ({
                                      ...prev,
                                      productId: value,
                                      productTitle: selectedProduct?.title
                                    }));
                                  }}
                                >
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {fan.savedProducts.map(product => (
                                      <SelectItem key={product.id} value={product.id}>
                                        {product.title} - €{product.base_price?.toFixed(2)}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            )}

                            {/* Discount Percentage */}
                            <div>
                              <Label className="mb-2 block flex items-center gap-2">
                                <Percent className="w-4 h-4 text-rose-500" />
                                Percentuale di Sconto
                              </Label>
                              <Select 
                                value={discountPercentage}
                                onValueChange={setDiscountPercentage}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="5">5%</SelectItem>
                                  <SelectItem value="10">10%</SelectItem>
                                  <SelectItem value="15">15%</SelectItem>
                                  <SelectItem value="20">20%</SelectItem>
                                  <SelectItem value="25">25%</SelectItem>
                                  <SelectItem value="30">30%</SelectItem>
                                  <SelectItem value="40">40%</SelectItem>
                                  <SelectItem value="50">50%</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>

                            {/* Personal Message */}
                            <div>
                              <Label className="mb-2 block">Messaggio personale (opzionale)</Label>
                              <Textarea
                                value={discountMessage}
                                onChange={(e) => setDiscountMessage(e.target.value)}
                                placeholder="Es: Ho notato il tuo interesse per questo prodotto..."
                                rows={3}
                              />
                            </div>

                            <div className="flex gap-3 pt-2">
                              <Button
                                type="button"
                                variant="outline"
                                className="flex-1"
                                onClick={() => {
                                  setOfferDiscountDialog(null);
                                  setDiscountMessage('');
                                }}
                              >
                                Annulla
                              </Button>
                              <Button
                                className="flex-1 bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white"
                                type="button"
                                onClick={handleSendDiscountOffer}
                                disabled={sendingOffer}
                              >
                                {sendingOffer ? (
                                  <>
                                    <div className="w-4 h-4 animate-spin rounded-full border-2 border-white border-t-transparent mr-2" />
                                    Invio...
                                  </>
                                ) : (
                                  <>
                                    <Send className="w-4 h-4 mr-2" />
                                    Invia Offerta
                                  </>
                                )}
                              </Button>
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="p-8 text-center">
              <Heart className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h4 className="font-semibold text-slate-700 mb-2">Nessun utente interessato ancora</h4>
              <p className="text-sm text-slate-500 max-w-md mx-auto">
                Quando gli utenti salveranno i tuoi prodotti tra i preferiti, li vedrai qui. 
                Potrai contattarli e proporre sconti esclusivi per convertirli in clienti!
              </p>
            </Card>
          )}
        </div>
      )}

      {/* RICHIESTE */}
      {mainTab === 'requests' && (
        <div className="mt-4">
          <h3 className="text-lg font-bold mb-4">Richieste per Te</h3>
          
          {/* Premium notice for free users */}
          {!isPremiumUser && matchingRequests.length > getVisibleRequests(matchingRequests).length && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-3">
              <Clock className="w-5 h-5 text-amber-600 flex-shrink-0" />
              <div className="text-sm text-amber-800">
                <span className="font-medium">Alcune richieste sono nascoste.</span> Con Premium vedi tutte le richieste immediatamente, senza attendere 24 ore.
              </div>
            </div>
          )}
          
          {getVisibleRequests(matchingRequests).length > 0 ? (
            <div className="space-y-3">
              {getVisibleRequests(matchingRequests).map((request) => (
                <Card key={request.id} className="p-4">
                  <h3 className="font-semibold mb-1">{request.title}</h3>
                  <p className="text-sm text-slate-600 mb-2">{request.description}</p>
                  <div className="flex items-center gap-3 text-xs mb-3">
                    <Badge variant="outline">{request.category}</Badge>
                    <span>{request.price_range}</span>
                    <span>{request.timeline}</span>
                  </div>
                  <div className="flex gap-2">
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button size="sm" className="bg-[#10B981] text-white"><Check className="w-3 h-3 mr-1" />Accetta</Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader><DialogTitle>Accetta Commessa</DialogTitle></DialogHeader>
                        <div className="space-y-4 mt-4">
                          <Textarea value={responseMessage} onChange={(e) => setResponseMessage(e.target.value)} placeholder="Messaggio..." rows={3} />
                          <Button onClick={() => handleRespondToRequest(request.id, 'accept')} className="w-full bg-[#10B981] text-white">Conferma</Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button size="sm" variant="outline"><X className="w-3 h-3 mr-1" />Rifiuta</Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader><DialogTitle>Rifiuta Richiesta</DialogTitle></DialogHeader>
                        <div className="space-y-4 mt-4">
                          <Textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Motivo..." rows={3} required />
                          <Button onClick={() => handleRespondToRequest(request.id, 'reject')} variant="destructive" className="w-full">Conferma Rifiuto</Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="p-8 text-center">
              <Sparkles className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-600">Nessuna richiesta</p>
            </Card>
          )}
        </div>
      )}

      {/* PROPOSTE */}
      {mainTab === 'proposals' && (
        <div className="mt-4">
          {(() => {
            const pendingProposals = proposals.filter(p => p.status === 'pending');
            const historyProposals = proposals.filter(p => p.status === 'accepted' || p.status === 'rejected');
            
            return (
              <div>
                {/* Sub-navigation pills for proposal sections */}
                <div className="flex gap-2 mb-6">
                  <button
                    onClick={() => setProposalsView('pending')}
                    data-testid="crafter-proposals-tab-pending"
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all ${
                      proposalsView === 'pending'
                        ? 'bg-[#DAA520] text-white shadow-lg shadow-[#DAA520]/25'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    <Clock className="w-4 h-4" />
                    In Attesa
                    {pendingProposals.length > 0 && (
                      <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${
                        proposalsView === 'pending' ? 'bg-white/20' : 'bg-[#DAA520] text-white'
                      }`}>
                        {pendingProposals.length}
                      </span>
                    )}
                  </button>
                  <button
                    onClick={() => setProposalsView('history')}
                    data-testid="crafter-proposals-tab-history"
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all ${
                      proposalsView === 'history'
                        ? 'bg-slate-600 text-white shadow-lg shadow-slate-600/25'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    <History className="w-4 h-4" />
                    Storico
                    {historyProposals.length > 0 && (
                      <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${
                        proposalsView === 'history' ? 'bg-white/20' : 'bg-slate-500 text-white'
                      }`}>
                        {historyProposals.length}
                      </span>
                    )}
                  </button>
                </div>

                {/* Proposte in attesa */}
                {proposalsView === 'pending' && (
                  <>
                    {pendingProposals.length > 0 ? (
                      <div className="space-y-3">
                        {pendingProposals.map((proposal) => {
                          const isExpanded = expandedProposalId === proposal.id;
                          const request = proposal.direct_requests;
                          
                          return (
                            <Card 
                              key={proposal.id} 
                              className={`overflow-hidden border-l-4 border-l-amber-400 transition-all ${isExpanded ? 'ring-2 ring-amber-300/50' : ''}`}
                            >
                              {/* Header - Always visible, clickable */}
                              <button
                                onClick={() => setExpandedProposalId(isExpanded ? null : proposal.id)}
                                className="w-full p-4 flex items-center gap-4 hover:bg-amber-50/50 transition-colors text-left"
                              >
                                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                                  <Clock className="w-5 h-5 text-amber-600" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap mb-1">
                                    <span className="font-semibold truncate">{request?.title || 'Richiesta'}</span>
                                    <Badge className="bg-amber-100 text-amber-700 text-xs">In Attesa</Badge>
                                  </div>
                                  <div className="flex items-center gap-3 text-sm text-slate-500">
                                    {proposal.proposed_price > 0 && (
                                      <span className="font-bold text-[#DAA520]">€{proposal.proposed_price}</span>
                                    )}
                                    {proposal.estimated_timeline && (
                                      <span>{getTimelineLabel(proposal.estimated_timeline)}</span>
                                    )}
                                    <span className="text-xs">
                                      {new Date(proposal.created_at).toLocaleDateString('it-IT')}
                                    </span>
                                  </div>
                                </div>
                                <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                              </button>
                              
                              {/* Expanded content */}
                              {isExpanded && (
                                <div className="px-4 pb-4 border-t bg-amber-50/30">
                                  <div className="pt-4 space-y-4">
                                    {/* Il tuo messaggio */}
                                    <div>
                                      <h4 className="text-xs font-semibold text-slate-500 uppercase mb-2">La tua proposta</h4>
                                      <p className="text-sm text-slate-700 bg-white p-3 rounded-lg border">{proposal.message}</p>
                                    </div>
                                    
                                    {/* Dettagli proposta */}
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                      <div className="bg-white p-3 rounded-lg border">
                                        <p className="text-xs text-slate-500 mb-1">Prezzo proposto</p>
                                        <p className="font-bold text-[#DAA520]">€{proposal.proposed_price || 0}</p>
                                      </div>
                                      <div className="bg-white p-3 rounded-lg border">
                                        <p className="text-xs text-slate-500 mb-1">Tempistica</p>
                                        <p className="font-medium">{getTimelineLabel(proposal.estimated_timeline)}</p>
                                      </div>
                                      <div className="bg-white p-3 rounded-lg border">
                                        <p className="text-xs text-slate-500 mb-1">Spedizione</p>
                                        <p className="font-medium">{proposal.shipping_cost > 0 ? `€${proposal.shipping_cost}` : 'Inclusa'}</p>
                                      </div>
                                      <div className="bg-white p-3 rounded-lg border">
                                        <p className="text-xs text-slate-500 mb-1">Inviata il</p>
                                        <p className="font-medium">{new Date(proposal.created_at).toLocaleDateString('it-IT')}</p>
                                      </div>
                                    </div>
                                    
                                    {/* Dettagli richiesta originale */}
                                    {request && (
                                      <div className="border-t pt-4">
                                        <h4 className="text-xs font-semibold text-slate-500 uppercase mb-2">Richiesta del cliente</h4>
                                        <div className="bg-white p-3 rounded-lg border space-y-2">
                                          {request.description && (
                                            <p className="text-sm text-slate-600">{request.description}</p>
                                          )}
                                          <div className="flex flex-wrap gap-2 text-xs">
                                            {request.category && (
                                              <Badge variant="outline" className="bg-slate-50">
                                                {request.category}
                                              </Badge>
                                            )}
                                            {(request.budget_min || request.budget_max) && (
                                              <Badge variant="outline" className="bg-slate-50">
                                                Budget: €{request.budget_min || 0} - €{request.budget_max || '∞'}
                                              </Badge>
                                            )}
                                          </div>
                                          {request.images && request.images.length > 0 && (
                                            <div className="flex gap-2 mt-2">
                                              {request.images.slice(0, 3).map((img, idx) => (
                                                <img 
                                                  key={idx} 
                                                  src={img} 
                                                  alt={`Riferimento ${idx + 1}`}
                                                  className="w-16 h-16 object-cover rounded-lg border"
                                                />
                                              ))}
                                              {request.images.length > 3 && (
                                                <div className="w-16 h-16 bg-slate-100 rounded-lg border flex items-center justify-center text-sm text-slate-500">
                                                  +{request.images.length - 3}
                                                </div>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                    
                                    {/* Allegati proposta */}
                                    {proposal.attachments && proposal.attachments.length > 0 && (
                                      <div>
                                        <h4 className="text-xs font-semibold text-slate-500 uppercase mb-2">I tuoi allegati</h4>
                                        <div className="flex gap-2">
                                          {proposal.attachments.map((att, idx) => (
                                            <a 
                                              key={idx}
                                              href={att}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="w-16 h-16 bg-white rounded-lg border hover:border-amber-400 transition-colors overflow-hidden"
                                            >
                                              <img src={att} alt={`Allegato ${idx + 1}`} className="w-full h-full object-cover" />
                                            </a>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </Card>
                          );
                        })}
                      </div>
                    ) : (
                      <Card className="p-8 text-center bg-slate-50">
                        <Clock className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                        <p className="text-slate-500 font-medium">Nessuna proposta in attesa</p>
                        <p className="text-sm text-slate-400 mt-1">Le proposte inviate ai clienti appariranno qui</p>
                      </Card>
                    )}
                  </>
                )}

                {/* Storico proposte */}
                {proposalsView === 'history' && (
                  <>
                    {historyProposals.length > 0 ? (
                      <div className="space-y-3">
                        {historyProposals.map((proposal) => {
                          const isExpanded = expandedProposalId === proposal.id;
                          const request = proposal.direct_requests;
                          const isAccepted = proposal.status === 'accepted';
                          
                          return (
                            <Card 
                              key={proposal.id} 
                              className={`overflow-hidden border-l-4 transition-all ${
                                isAccepted 
                                  ? 'border-l-[#10B981]' 
                                  : 'border-l-slate-300'
                              } ${isExpanded ? (isAccepted ? 'ring-2 ring-[#10B981]/30' : 'ring-2 ring-slate-300/50') : ''}`}
                            >
                              {/* Header - Always visible, clickable */}
                              <button
                                onClick={() => setExpandedProposalId(isExpanded ? null : proposal.id)}
                                className={`w-full p-4 flex items-center gap-4 transition-colors text-left ${
                                  isAccepted ? 'hover:bg-[#10B981]/5' : 'hover:bg-slate-50'
                                }`}
                              >
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                                  isAccepted ? 'bg-[#10B981]/10' : 'bg-slate-100'
                                }`}>
                                  {isAccepted ? (
                                    <CheckCircle2 className="w-5 h-5 text-[#10B981]" />
                                  ) : (
                                    <XCircle className="w-5 h-5 text-slate-400" />
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap mb-1">
                                    <span className="font-semibold truncate">{request?.title || 'Richiesta'}</span>
                                    <Badge className={isAccepted ? 'bg-[#10B981] text-white text-xs' : 'bg-slate-200 text-slate-600 text-xs'}>
                                      {isAccepted ? 'Accettata' : 'Non selezionata'}
                                    </Badge>
                                  </div>
                                  <div className="flex items-center gap-3 text-sm text-slate-500">
                                    {proposal.proposed_price > 0 && (
                                      <span className={isAccepted ? 'font-bold text-[#10B981]' : 'font-medium'}> 
                                        €{proposal.proposed_price}
                                      </span>
                                    )}
                                    {proposal.estimated_timeline && (
                                      <span>{getTimelineLabel(proposal.estimated_timeline)}</span>
                                    )}
                                    <span className="text-xs">
                                      {new Date(proposal.created_at).toLocaleDateString('it-IT')}
                                    </span>
                                  </div>
                                </div>
                                <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                              </button>
                              
                              {/* Expanded content */}
                              {isExpanded && (
                                <div className={`px-4 pb-4 border-t ${isAccepted ? 'bg-[#10B981]/5' : 'bg-slate-50'}`}>
                                  <div className="pt-4 space-y-4">
                                    {/* Il tuo messaggio */}
                                    <div>
                                      <h4 className="text-xs font-semibold text-slate-500 uppercase mb-2">La tua proposta</h4>
                                      <p className="text-sm text-slate-700 bg-white p-3 rounded-lg border">{proposal.message}</p>
                                    </div>
                                    
                                    {/* Dettagli proposta */}
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                      <div className="bg-white p-3 rounded-lg border">
                                        <p className="text-xs text-slate-500 mb-1">Prezzo proposto</p>
                                        <p className={`font-bold ${isAccepted ? 'text-[#10B981]' : 'text-slate-700'}`}>€{proposal.proposed_price || 0}</p>
                                      </div>
                                      <div className="bg-white p-3 rounded-lg border">
                                        <p className="text-xs text-slate-500 mb-1">Tempistica</p>
                                        <p className="font-medium">{getTimelineLabel(proposal.estimated_timeline)}</p>
                                      </div>
                                      <div className="bg-white p-3 rounded-lg border">
                                        <p className="text-xs text-slate-500 mb-1">Spedizione</p>
                                        <p className="font-medium">{proposal.shipping_cost > 0 ? `€${proposal.shipping_cost}` : 'Inclusa'}</p>
                                      </div>
                                      <div className="bg-white p-3 rounded-lg border">
                                        <p className="text-xs text-slate-500 mb-1">Inviata il</p>
                                        <p className="font-medium">{new Date(proposal.created_at).toLocaleDateString('it-IT')}</p>
                                      </div>
                                    </div>
                                    
                                    {/* Dettagli richiesta originale */}
                                    {request && (
                                      <div className="border-t pt-4">
                                        <h4 className="text-xs font-semibold text-slate-500 uppercase mb-2">Richiesta del cliente</h4>
                                        <div className="bg-white p-3 rounded-lg border space-y-2">
                                          {request.description && (
                                            <p className="text-sm text-slate-600">{request.description}</p>
                                          )}
                                          <div className="flex flex-wrap gap-2 text-xs">
                                            {request.category && (
                                              <Badge variant="outline" className="bg-slate-50">
                                                {request.category}
                                              </Badge>
                                            )}
                                            {(request.budget_min || request.budget_max) && (
                                              <Badge variant="outline" className="bg-slate-50">
                                                Budget: €{request.budget_min || 0} - €{request.budget_max || '∞'}
                                              </Badge>
                                            )}
                                          </div>
                                          {request.images && request.images.length > 0 && (
                                            <div className="flex gap-2 mt-2">
                                              {request.images.slice(0, 3).map((img, idx) => (
                                                <img 
                                                  key={idx} 
                                                  src={img} 
                                                  alt={`Riferimento ${idx + 1}`}
                                                  className="w-16 h-16 object-cover rounded-lg border"
                                                />
                                              ))}
                                              {request.images.length > 3 && (
                                                <div className="w-16 h-16 bg-slate-100 rounded-lg border flex items-center justify-center text-sm text-slate-500">
                                                  +{request.images.length - 3}
                                                </div>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                    
                                    {/* Allegati proposta */}
                                    {proposal.attachments && proposal.attachments.length > 0 && (
                                      <div>
                                        <h4 className="text-xs font-semibold text-slate-500 uppercase mb-2">I tuoi allegati</h4>
                                        <div className="flex gap-2">
                                          {proposal.attachments.map((att, idx) => (
                                            <a 
                                              key={idx}
                                              href={att}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="w-16 h-16 bg-white rounded-lg border hover:border-[#10B981] transition-colors overflow-hidden"
                                            >
                                              <img src={att} alt={`Allegato ${idx + 1}`} className="w-full h-full object-cover" />
                                            </a>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </Card>
                          );
                        })}
                      </div>
                    ) : (
                      <Card className="p-8 text-center bg-slate-50">
                        <History className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                        <p className="text-slate-500 font-medium">Nessuna proposta nello storico</p>
                        <p className="text-sm text-slate-400 mt-1">Le proposte accettate o rifiutate appariranno qui</p>
                      </Card>
                    )}
                  </>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {/* ===================== TAB RICHIESTE DIRETTE ===================== */}
      {mainTab === 'direct-requests' && (
        <div className="mt-4">
          {(() => {
            // Richieste in attesa = open o pending
            const pendingRequests = directRequests.filter(r => r.status === 'open' || r.status === 'pending');
            // Storico = in_progress (trasformate in ordine), completed, closed, rejected
            const historyRequests = directRequests.filter(r => ['in_progress', 'completed', 'closed', 'rejected', 'accepted'].includes(r.status));
            
            return (
              <div>
                {/* Sub-navigation pills */}
                <div className="flex gap-2 mb-6">
                  <button
                    onClick={() => setDirectRequestsView('pending')}
                    data-testid="crafter-requests-tab-pending"
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all ${
                      directRequestsView === 'pending'
                        ? 'bg-[#008080] text-white shadow-lg shadow-[#008080]/25'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    <Inbox className="w-4 h-4" />
                    In Attesa
                    {pendingRequests.length > 0 && (
                      <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${
                        directRequestsView === 'pending' ? 'bg-white/20' : 'bg-[#008080] text-white'
                      }`}>
                        {pendingRequests.length}
                      </span>
                    )}
                  </button>
                  <button
                    onClick={() => setDirectRequestsView('history')}
                    data-testid="crafter-requests-tab-history"
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all ${
                      directRequestsView === 'history'
                        ? 'bg-slate-600 text-white shadow-lg shadow-slate-600/25'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    <History className="w-4 h-4" />
                    Storico
                    {historyRequests.length > 0 && (
                      <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${
                        directRequestsView === 'history' ? 'bg-white/20' : 'bg-slate-500 text-white'
                      }`}>
                        {historyRequests.length}
                      </span>
                    )}
                  </button>
                </div>

                {/* Richieste in attesa */}
                {directRequestsView === 'pending' && (
                  <>
                    {pendingRequests.length > 0 ? (
                      <div className="space-y-3">
                        {pendingRequests.map((request) => {
                          const isExpanded = expandedRequestId === request.id;
                          
                          return (
                            <Card 
                              key={request.id} 
                              className={`overflow-hidden border-l-4 border-l-[#008080] transition-all ${isExpanded ? 'ring-2 ring-[#008080]/30' : ''}`}
                            >
                              {/* Header - Always visible, clickable */}
                              <button
                                onClick={() => setExpandedRequestId(isExpanded ? null : request.id)}
                                className="w-full p-4 flex items-center gap-4 hover:bg-[#008080]/5 transition-colors text-left"
                              >
                                <img
                                  src={request.client?.avatar_url || '/placeholder-avatar.png'}
                                  alt={request.client?.full_name}
                                  className="w-12 h-12 rounded-full object-cover flex-shrink-0 border-2 border-[#008080]/20"
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap mb-1">
                                    <span className="font-semibold truncate">{request.title}</span>
                                    <Badge className="bg-[#008080]/10 text-[#008080] text-xs">Aperta</Badge>
                                    {request.gift_event && (
                                      <Badge variant="outline" className="text-xs bg-rose-50 text-rose-700 border-rose-200">
                                        <Gift className="w-3 h-3 mr-1" />
                                        Regalo
                                      </Badge>
                                    )}
                                    {request.source === 'shop' && (
                                      <Badge variant="outline" className="text-xs bg-amber-50 text-amber-800 border-amber-200" title="Il cliente è arrivato dal link o dal QR del tuo negozio">
                                        <Store className="w-3 h-3 mr-1" />
                                        Dal tuo negozio
                                      </Badge>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-3 text-sm text-slate-500">
                                    <span className="flex items-center gap-1">
                                      <User className="w-3 h-3" />
                                      {request.client?.full_name || 'Cliente'}
                                    </span>
                                    {request.price_range && (
                                      <span className="flex items-center gap-1">
                                        <Euro className="w-3 h-3" />
                                        {request.price_range}
                                      </span>
                                    )}
                                    <span className="text-xs">
                                      {new Date(request.created_at).toLocaleDateString('it-IT')}
                                    </span>
                                  </div>
                                </div>
                                <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                              </button>
                              
                              {/* Expanded content */}
                              {isExpanded && (
                                <div className="px-4 pb-4 border-t bg-[#008080]/5">
                                  <div className="pt-4 space-y-4">
                                    {/* Descrizione richiesta */}
                                    <div>
                                      <h4 className="text-xs font-semibold text-slate-500 uppercase mb-2">Descrizione richiesta</h4>
                                      <p className="text-sm text-slate-700 bg-white p-3 rounded-lg border">{request.description || 'Nessuna descrizione'}</p>
                                    </div>
                                    
                                    {/* Dettagli */}
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                      <div className="bg-white p-3 rounded-lg border">
                                        <p className="text-xs text-slate-500 mb-1">Budget</p>
                                        <p className="font-bold text-[#008080]">{request.price_range || 'Non specificato'}</p>
                                      </div>
                                      <div className="bg-white p-3 rounded-lg border">
                                        <p className="text-xs text-slate-500 mb-1">Tempistica</p>
                                        <p className="font-medium">{getTimelineLabel(request.timeline) || 'Flessibile'}</p>
                                      </div>
                                      <div className="bg-white p-3 rounded-lg border">
                                        <p className="text-xs text-slate-500 mb-1">Categoria</p>
                                        <p className="font-medium">{request.category || '-'}</p>
                                      </div>
                                      <div className="bg-white p-3 rounded-lg border">
                                        <p className="text-xs text-slate-500 mb-1">Ricevuta il</p>
                                        <p className="font-medium">{new Date(request.created_at).toLocaleDateString('it-IT')}</p>
                                      </div>
                                    </div>
                                    
                                    {/* Info regalo se presente */}
                                    {request.gift_event && (
                                      <div className="bg-rose-50 p-3 rounded-lg border border-rose-200">
                                        <h4 className="text-xs font-semibold text-rose-700 uppercase mb-2 flex items-center gap-1">
                                          <Gift className="w-3 h-3" />
                                          Dettagli Regalo
                                        </h4>
                                        <div className="flex gap-4 text-sm">
                                          <span><strong>Evento:</strong> {request.gift_event}</span>
                                          {request.gift_recipient && <span><strong>Per:</strong> {request.gift_recipient}</span>}
                                          {request.gift_date && <span><strong>Data:</strong> {new Date(request.gift_date).toLocaleDateString('it-IT')}</span>}
                                        </div>
                                      </div>
                                    )}
                                    
                                    {/* Immagini di riferimento */}
                                    {request.images && request.images.length > 0 && (
                                      <div>
                                        <h4 className="text-xs font-semibold text-slate-500 uppercase mb-2">Immagini di riferimento</h4>
                                        <div className="flex gap-2 flex-wrap">
                                          {request.images.map((img, idx) => (
                                            <a 
                                              key={idx}
                                              href={img}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="w-20 h-20 bg-white rounded-lg border hover:border-[#008080] transition-colors overflow-hidden"
                                            >
                                              <img src={img} alt={`Riferimento ${idx + 1}`} className="w-full h-full object-cover" />
                                            </a>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                    
                                    {/* Azioni */}
                                    <div className="flex gap-2 pt-2 border-t">
                                      <Button
                                        size="sm"
                                        className="bg-[#DAA520] text-white hover:bg-[#DAA520]/90"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setRespondingToRequest(request);
                                        }}
                                        data-testid={`respond-request-${request.id}`}
                                      >
                                        <Send className="w-4 h-4 mr-1" />
                                        Invia Proposta
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          const { data: existingConv } = await supabase
                                            .from('conversations')
                                            .select('id')
                                            .or(`and(participant_1.eq.${user.id},participant_2.eq.${request.client_id}),and(participant_1.eq.${request.client_id},participant_2.eq.${user.id})`)
                                            .single();

                                          if (existingConv) {
                                            navigate(`/messages/${existingConv.id}`);
                                          } else {
                                            const { data: newConv } = await supabase
                                              .from('conversations')
                                              .insert({
                                                participant_1: user.id,
                                                participant_2: request.client_id
                                              })
                                              .select()
                                              .single();
                                            if (newConv) navigate(`/messages/${newConv.id}`);
                                          }
                                        }}
                                      >
                                        <MessageSquare className="w-4 h-4 mr-1" />
                                        Messaggia
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </Card>
                          );
                        })}
                      </div>
                    ) : (
                      <Card className="p-8 text-center bg-slate-50">
                        <Inbox className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                        <p className="text-slate-500 font-medium">Nessuna richiesta in attesa</p>
                        <p className="text-sm text-slate-400 mt-1">Le richieste dei clienti appariranno qui</p>
                      </Card>
                    )}
                  </>
                )}

                {/* Storico richieste */}
                {directRequestsView === 'history' && (
                  <>
                    {historyRequests.length > 0 ? (
                      <div className="space-y-3">
                        {historyRequests.map((request) => {
                          const isExpanded = expandedRequestId === request.id;
                          const isAccepted = request.status === 'in_progress' || request.status === 'accepted' || request.status === 'completed';
                          
                          return (
                            <Card 
                              key={request.id} 
                              className={`overflow-hidden border-l-4 transition-all ${
                                isAccepted 
                                  ? 'border-l-[#10B981]' 
                                  : 'border-l-slate-300'
                              } ${isExpanded ? (isAccepted ? 'ring-2 ring-[#10B981]/30' : 'ring-2 ring-slate-300/50') : ''}`}
                            >
                              {/* Header - Always visible, clickable */}
                              <button
                                onClick={() => setExpandedRequestId(isExpanded ? null : request.id)}
                                className={`w-full p-4 flex items-center gap-4 transition-colors text-left ${
                                  isAccepted ? 'hover:bg-[#10B981]/5' : 'hover:bg-slate-50'
                                }`}
                              >
                                <img
                                  src={request.client?.avatar_url || '/placeholder-avatar.png'}
                                  alt={request.client?.full_name}
                                  className={`w-12 h-12 rounded-full object-cover flex-shrink-0 border-2 ${
                                    isAccepted ? 'border-[#10B981]/30' : 'border-slate-200'
                                  }`}
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap mb-1">
                                    <span className="font-semibold truncate">{request.title}</span>
                                    <Badge className={isAccepted ? 'bg-[#10B981] text-white text-xs' : 'bg-slate-200 text-slate-600 text-xs'}>
                                      {request.status === 'in_progress' ? 'In Lavorazione' : 
                                       request.status === 'completed' ? 'Completata' :
                                       request.status === 'accepted' ? 'Accettata' :
                                       request.status === 'closed' ? 'Chiusa' :
                                       'Non accettata'}
                                    </Badge>
                                    {request.gift_event && (
                                      <Badge variant="outline" className="text-xs bg-rose-50 text-rose-700 border-rose-200">
                                        <Gift className="w-3 h-3 mr-1" />
                                        Regalo
                                      </Badge>
                                    )}
                                    {request.source === 'shop' && (
                                      <Badge variant="outline" className="text-xs bg-amber-50 text-amber-800 border-amber-200" title="Il cliente è arrivato dal link o dal QR del tuo negozio">
                                        <Store className="w-3 h-3 mr-1" />
                                        Dal tuo negozio
                                      </Badge>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-3 text-sm text-slate-500">
                                    <span className="flex items-center gap-1">
                                      <User className="w-3 h-3" />
                                      {request.client?.full_name || 'Cliente'}
                                    </span>
                                    {request.price_range && (
                                      <span className={isAccepted ? 'font-bold text-[#10B981]' : 'font-medium'}>
                                        {request.price_range}
                                      </span>
                                    )}
                                    <span className="text-xs">
                                      {new Date(request.created_at).toLocaleDateString('it-IT')}
                                    </span>
                                  </div>
                                </div>
                                <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                              </button>
                              
                              {/* Expanded content */}
                              {isExpanded && (
                                <div className={`px-4 pb-4 border-t ${isAccepted ? 'bg-[#10B981]/5' : 'bg-slate-50'}`}>
                                  <div className="pt-4 space-y-4">
                                    {/* Descrizione richiesta */}
                                    <div>
                                      <h4 className="text-xs font-semibold text-slate-500 uppercase mb-2">Descrizione richiesta</h4>
                                      <p className="text-sm text-slate-700 bg-white p-3 rounded-lg border">{request.description || 'Nessuna descrizione'}</p>
                                    </div>
                                    
                                    {/* Dettagli */}
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                      <div className="bg-white p-3 rounded-lg border">
                                        <p className="text-xs text-slate-500 mb-1">Budget</p>
                                        <p className={`font-bold ${isAccepted ? 'text-[#10B981]' : 'text-slate-700'}`}>{request.price_range || 'Non specificato'}</p>
                                      </div>
                                      <div className="bg-white p-3 rounded-lg border">
                                        <p className="text-xs text-slate-500 mb-1">Tempistica</p>
                                        <p className="font-medium">{getTimelineLabel(request.timeline) || 'Flessibile'}</p>
                                      </div>
                                      <div className="bg-white p-3 rounded-lg border">
                                        <p className="text-xs text-slate-500 mb-1">Categoria</p>
                                        <p className="font-medium">{request.category || '-'}</p>
                                      </div>
                                      <div className="bg-white p-3 rounded-lg border">
                                        <p className="text-xs text-slate-500 mb-1">Ricevuta il</p>
                                        <p className="font-medium">{new Date(request.created_at).toLocaleDateString('it-IT')}</p>
                                      </div>
                                    </div>
                                    
                                    {/* Info regalo se presente */}
                                    {request.gift_event && (
                                      <div className="bg-rose-50 p-3 rounded-lg border border-rose-200">
                                        <h4 className="text-xs font-semibold text-rose-700 uppercase mb-2 flex items-center gap-1">
                                          <Gift className="w-3 h-3" />
                                          Dettagli Regalo
                                        </h4>
                                        <div className="flex gap-4 text-sm">
                                          <span><strong>Evento:</strong> {request.gift_event}</span>
                                          {request.gift_recipient && <span><strong>Per:</strong> {request.gift_recipient}</span>}
                                          {request.gift_date && <span><strong>Data:</strong> {new Date(request.gift_date).toLocaleDateString('it-IT')}</span>}
                                        </div>
                                      </div>
                                    )}
                                    
                                    {/* Immagini di riferimento */}
                                    {request.images && request.images.length > 0 && (
                                      <div>
                                        <h4 className="text-xs font-semibold text-slate-500 uppercase mb-2">Immagini di riferimento</h4>
                                        <div className="flex gap-2 flex-wrap">
                                          {request.images.map((img, idx) => (
                                            <a 
                                              key={idx}
                                              href={img}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="w-20 h-20 bg-white rounded-lg border hover:border-[#10B981] transition-colors overflow-hidden"
                                            >
                                              <img src={img} alt={`Riferimento ${idx + 1}`} className="w-full h-full object-cover" />
                                            </a>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                    
                                    {/* Link all'ordine se in lavorazione */}
                                    {(request.status === 'in_progress' || request.status === 'completed') && (
                                      <div className="flex gap-2 pt-2 border-t">
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          className="border-[#10B981] text-[#10B981] hover:bg-[#10B981]/10"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            // Navigate to orders section
                                            setMainTab('orders');
                                          }}
                                        >
                                          <Package className="w-4 h-4 mr-1" />
                                          Vai all'Ordine
                                        </Button>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </Card>
                          );
                        })}
                      </div>
                    ) : (
                      <Card className="p-8 text-center bg-slate-50">
                        <History className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                        <p className="text-slate-500 font-medium">Nessuna richiesta nello storico</p>
                        <p className="text-sm text-slate-400 mt-1">Le richieste trasformate in ordini appariranno qui</p>
                      </Card>
                    )}
                  </>
                )}
              </div>
            );
          })()}

          {/* Response Dialog */}
          <Dialog open={!!respondingToRequest} onOpenChange={() => setRespondingToRequest(null)}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Send className="w-5 h-5 text-[#DAA520]" />
                  Invia la tua Proposta
                </DialogTitle>
              </DialogHeader>
              
              {respondingToRequest && (
                <div className="space-y-4 py-4">
                  <div className="p-3 bg-slate-50 rounded-lg text-sm">
                    <p className="font-medium">{respondingToRequest.title}</p>
                    <p className="text-slate-500 text-xs mt-1">
                      Da: {respondingToRequest.client?.full_name}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium">Prezzo (€) *</Label>
                      <Input
                        type="number"
                        value={requestResponseForm.price}
                        onChange={(e) => setRequestResponseForm({...requestResponseForm, price: e.target.value})}
                        placeholder="Es: 150"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Tempi</Label>
                      <select
                        value={requestResponseForm.timeline}
                        onChange={(e) => setRequestResponseForm({...requestResponseForm, timeline: e.target.value})}
                        className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm"
                      >
                        <option value="">Seleziona...</option>
                        <option value="1 settimana">1 settimana</option>
                        <option value="2 settimane">2 settimane</option>
                        <option value="1 mese">1 mese</option>
                        <option value="2 mesi">2 mesi</option>
                        <option value="Da concordare">Da concordare</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <Label className="text-sm font-medium">Messaggio *</Label>
                    <Textarea
                      value={requestResponseForm.message}
                      onChange={(e) => setRequestResponseForm({...requestResponseForm, message: e.target.value})}
                      placeholder="Descrivi la tua proposta, materiali, dettagli..."
                      className="mt-1 min-h-[100px]"
                    />
                  </div>

                  {/* Image Upload for Proposal */}
                  <div>
                    <Label className="text-sm font-medium">Immagini di riferimento (opzionale)</Label>
                    <div className="mt-2">
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleResponseFileUpload}
                        className="hidden"
                        id="response-upload"
                        disabled={uploadingResponseAttachment}
                      />
                      <label
                        htmlFor="response-upload"
                        className={`flex flex-col items-center justify-center w-full h-20 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                          uploadingResponseAttachment ? 'bg-slate-100 border-slate-300' : 'border-slate-300 hover:border-[#DAA520] hover:bg-amber-50'
                        }`}
                      >
                        {uploadingResponseAttachment ? (
                          <Loader2 className="w-5 h-5 text-slate-400 animate-spin" />
                        ) : (
                          <>
                            <Upload className="w-5 h-5 text-slate-400 mb-1" />
                            <span className="text-xs text-slate-500">Carica immagini (max 5)</span>
                          </>
                        )}
                      </label>
                    </div>
                    
                    {/* Thumbnails */}
                    {responseAttachments.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {responseAttachments.map((att, idx) => (
                          <div key={idx} className="relative group">
                            <img 
                              src={att.url} 
                              alt={att.name}
                              className="w-14 h-14 object-cover rounded-lg border"
                            />
                            <button
                              type="button"
                              onClick={() => removeResponseAttachment(idx)}
                              className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X className="w-2.5 h-2.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Shipping Cost */}
                  <div className="border border-slate-200 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-sm font-medium">Spese di spedizione</Label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={requestResponseForm.freeShipping}
                          onChange={(e) => setRequestResponseForm({
                            ...requestResponseForm, 
                            freeShipping: e.target.checked,
                            shippingCost: e.target.checked ? '' : requestResponseForm.shippingCost
                          })}
                          className="w-4 h-4 rounded border-slate-300 text-[#008080] focus:ring-[#008080]"
                        />
                        <span className="text-xs text-slate-600">Spedizione gratuita</span>
                      </label>
                    </div>
                    {!requestResponseForm.freeShipping && (
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">€</span>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={requestResponseForm.shippingCost}
                          onChange={(e) => setRequestResponseForm({...requestResponseForm, shippingCost: e.target.value})}
                          placeholder="0.00"
                          className="pl-7"
                        />
                      </div>
                    )}
                    {requestResponseForm.freeShipping && (
                      <p className="text-xs text-green-600 flex items-center gap-1">
                        <Truck className="w-3 h-3" />
                        Il cliente non pagherà spese di spedizione
                      </p>
                    )}
                  </div>

                  <Button
                    onClick={handleRespondToDirectRequest}
                    disabled={sendingRequestResponse || !requestResponseForm.price || !requestResponseForm.message}
                    className="w-full bg-[#DAA520] text-white hover:bg-[#DAA520]/90"
                  >
                    {sendingRequestResponse ? 'Invio in corso...' : 'Invia Proposta'}
                  </Button>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>
      )}

      {/* ===================== TAB FEEDBACK ===================== */}
      {mainTab === 'feedback' && (
        <div className="mt-4">
          {/* Stats Summary */}
          {reviews.length > 0 && (
            <Card className="p-6 mb-6 bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-white rounded-xl shadow-sm flex flex-col items-center justify-center">
                    <span className="text-2xl font-bold text-amber-600">
                      {(reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)}
                    </span>
                    <div className="flex">
                      {[1,2,3,4,5].map(i => (
                        <Star key={i} className={`w-3 h-3 ${
                          i <= Math.round(reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length)
                            ? 'fill-amber-400 text-amber-400'
                            : 'text-slate-200'
                        }`} />
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-lg font-semibold text-slate-800">Media recensioni</p>
                    <p className="text-sm text-slate-600">{reviews.length} recension{reviews.length === 1 ? 'e' : 'i'} totali</p>
                  </div>
                </div>
                <div className="flex gap-6">
                  {[5, 4, 3, 2, 1].map(star => {
                    const count = reviews.filter(r => r.rating === star).length;
                    return (
                      <div key={star} className="text-center">
                        <div className="flex items-center gap-1 text-sm">
                          <span className="font-medium">{star}</span>
                          <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                        </div>
                        <p className="text-xs text-slate-500">{count}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </Card>
          )}

          {/* Reviews List */}
          {reviews.length > 0 ? (
            <div className="space-y-4">
              {reviews.map((review) => (
                <Card key={review.id} className="p-5" data-testid={`feedback-${review.id}`}>
                  <div className="flex items-start gap-4">
                    {/* Client Avatar */}
                    <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                      {review.client?.avatar_url ? (
                        <img 
                          src={review.client.avatar_url} 
                          alt="" 
                          className="w-full h-full rounded-full object-cover"
                        />
                      ) : (
                        <User className="w-6 h-6 text-slate-400" />
                      )}
                    </div>

                    {/* Review Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between flex-wrap gap-2">
                        <div>
                          <p className="font-semibold">{review.client?.full_name || 'Cliente'}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <div className="flex">
                              {[1,2,3,4,5].map(i => (
                                <Star key={i} className={`w-4 h-4 ${
                                  i <= review.rating
                                    ? 'fill-amber-400 text-amber-400'
                                    : 'text-slate-200'
                                }`} />
                              ))}
                            </div>
                            <Badge variant="outline" className="text-xs text-green-600 border-green-200">
                              <CheckCircle2 className="w-3 h-3 mr-1" />
                              Acquisto verificato
                            </Badge>
                          </div>
                        </div>
                        <span className="text-xs text-slate-400">
                          {new Date(review.created_at).toLocaleDateString('it-IT')}
                        </span>
                      </div>

                      {/* Product Info - Enhanced */}
                      {review.product ? (
                        <a 
                          href={productUrl(review.product)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-3 flex items-center gap-3 p-2 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors group"
                        >
                          {/* Product Thumbnail */}
                          <div className="w-12 h-12 rounded-lg overflow-hidden bg-white border border-slate-200 flex-shrink-0">
                            {review.product.images?.[0] ? (
                              <img 
                                src={review.product.images[0]} 
                                alt={review.product.title}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Package className="w-5 h-5 text-slate-300" />
                              </div>
                            )}
                          </div>
                          {/* Product Title */}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-700 truncate group-hover:text-[#008080] transition-colors">
                              {review.product.title}
                            </p>
                            <p className="text-xs text-slate-400 flex items-center gap-1">
                              <ExternalLink className="w-3 h-3" />
                              Apri prodotto
                            </p>
                          </div>
                        </a>
                      ) : (
                        <div className="mt-3 flex items-center gap-2 p-2 bg-slate-50 rounded-lg text-slate-400">
                          <Package className="w-4 h-4" />
                          <span className="text-xs">Prodotto non più disponibile</span>
                        </div>
                      )}

                      {/* Review Title & Comment */}
                      {review.title && (
                        <h4 className="font-semibold mt-3">{review.title}</h4>
                      )}
                      {review.comment && (
                        <p className="text-sm text-slate-600 mt-1">{review.comment}</p>
                      )}

                      {/* Sub-ratings Display */}
                      {(review.rating_quality || review.rating_communication || review.rating_shipping || review.rating_packaging || review.rating_value) && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {review.rating_quality && (
                            <span className="text-xs bg-slate-100 px-2 py-1 rounded-full text-slate-600">
                              Qualità: {review.rating_quality}/5
                            </span>
                          )}
                          {review.rating_communication && (
                            <span className="text-xs bg-slate-100 px-2 py-1 rounded-full text-slate-600">
                              Comunicazione: {review.rating_communication}/5
                            </span>
                          )}
                          {review.rating_shipping && (
                            <span className="text-xs bg-slate-100 px-2 py-1 rounded-full text-slate-600">
                              Consegna: {review.rating_shipping}/5
                            </span>
                          )}
                          {review.rating_packaging && (
                            <span className="text-xs bg-slate-100 px-2 py-1 rounded-full text-slate-600">
                              Imballaggio: {review.rating_packaging}/5
                            </span>
                          )}
                          {review.rating_value && (
                            <span className="text-xs bg-slate-100 px-2 py-1 rounded-full text-slate-600">
                              Rapporto Q/P: {review.rating_value}/5
                            </span>
                          )}
                        </div>
                      )}

                      {/* Customer Images */}
                      {review.images && review.images.length > 0 && (
                        <div className="mt-3">
                          <p className="text-xs text-slate-500 mb-2">Foto del cliente:</p>
                          <div className="flex flex-wrap gap-2">
                            {review.images.map((img, idx) => (
                              <a
                                key={idx}
                                href={img}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-16 h-16 rounded-lg overflow-hidden border border-slate-200 hover:border-[#008080] transition-colors"
                              >
                                <img
                                  src={img}
                                  alt={`Foto ${idx + 1}`}
                                  className="w-full h-full object-cover"
                                />
                              </a>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Crafter Response */}
                      {review.crafter_response ? (
                        <div className="mt-4 pl-4 border-l-2 border-[#008080]/30 bg-[#008080]/5 rounded-r-lg p-3">
                          <p className="text-xs font-medium text-[#008080] mb-1 flex items-center gap-1">
                            <Reply className="w-3 h-3" />
                            La tua risposta
                          </p>
                          <p className="text-sm text-slate-600">{review.crafter_response}</p>
                          <p className="text-xs text-slate-400 mt-1">
                            {new Date(review.crafter_response_at).toLocaleDateString('it-IT')}
                          </p>
                        </div>
                      ) : (
                        // Response Form
                        respondingToReview === review.id ? (
                          <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                            <Label className="text-sm font-medium mb-2 block">
                              Rispondi alla recensione
                            </Label>
                            <Textarea
                              value={responseText}
                              onChange={(e) => setResponseText(e.target.value)}
                              placeholder="Ringrazia il cliente o rispondi al suo feedback..."
                              rows={3}
                              className="mb-3"
                              maxLength={500}
                            />
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-slate-400">{responseText.length}/500</span>
                              <div className="flex gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setRespondingToReview(null);
                                    setResponseText('');
                                  }}
                                >
                                  Annulla
                                </Button>
                                <Button
                                  size="sm"
                                  className="bg-[#008080] text-white"
                                  onClick={() => handleSendResponse(review.id)}
                                  disabled={sendingResponse || !responseText.trim()}
                                >
                                  {sendingResponse ? (
                                    <>Invio...</>
                                  ) : (
                                    <>
                                      <Send className="w-4 h-4 mr-1" />
                                      Invia
                                    </>
                                  )}
                                </Button>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            className="mt-3"
                            onClick={() => setRespondingToReview(review.id)}
                          >
                            <Reply className="w-4 h-4 mr-1" />
                            Rispondi
                          </Button>
                        )
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="p-8 text-center border-dashed">
              <Star className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-600 mb-2">Nessuna recensione ricevuta</p>
              <p className="text-sm text-slate-500">
                Le recensioni dei clienti appariranno qui dopo che avranno completato i loro ordini
              </p>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
